// @ts-strict-ignore
import { Observable, of, Subject, throwError } from 'rxjs';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { Injectable, NgZone } from '@angular/core';
import { LMFormatUtilService, LMUrlUtilService } from '@services/utils';
import { SohoAlertDirective } from 'ids-enterprise-ng';
import { LMThemeService } from '@banner/lm-theme.service';
import { LMModel } from '@form/model';
import { LMRepositoryHelperComponent } from '@lplEditor/lm-repository-helper.component';
import { LMDialogButtonKey, LMUniqueIdService } from '@app/utils';
import { LMFormRequestService } from '@app/components';
import { environment } from '@environments/environment';
import IMarkerData = monaco.editor.IMarkerData;
import ITextModel = monaco.editor.ITextModel;
import CompletionList = monaco.languages.CompletionList;
import ProviderResult = monaco.languages.ProviderResult;
import CompletionContext = monaco.languages.CompletionContext;
import Position = monaco.Position;
import IEditorModel = monaco.editor.IEditorModel;
import { LMModalDialogWrapperRef, LMModalWrapperService } from '@app/services';
import { CancellationToken } from "monaco-editor";

@Injectable()
export class LMLplEditorService {

  public static LPL_VALIDATION_REQUEST = 'validateLPL';
  public loadingMonacoFinished: Subject<void> = new Subject<void>();
  private monacoLoaded = false;
  static SecClassTypeKeyWords: Array<SecClassType> = [ 'Module', 'DataArea', 'WebApp', 'Menu', 'MenuItem',
    'KeyField', 'BusinessClass', 'Field', 'FieldGroups', 'BusinessTask', 'Executable' ];
  static SecClassObjectKeyWords: Array<SecClassObject> = [ 'Module', 'DataArea', 'WebApp', 'Menu', 'MenuItem',
    'KeyField', 'BusinessClass', 'Field', 'AllFields', 'BusinessTask', 'Executable' ];

  constructor(
    protected httpClient: HttpClient,
    protected zone: NgZone,
    protected lmUrlUtilService: LMUrlUtilService,
    protected lmThemeService: LMThemeService,
    protected lmUniqueIdService: LMUniqueIdService,
    protected lmFormRequestService: LMFormRequestService,
    protected lmFormatUtilService: LMFormatUtilService,
    protected lmModalWrapperService: LMModalWrapperService) {
    this.loadMonaco();
  }

  private finishLoadingMonaco() {
    this.monacoLoaded = true;
    this.loadingMonacoFinished.next();
  }

  private loadMonaco() {
    // load the assets
    const baseUrl = environment.production ? '/static/cli/dist/assets/monaco/min/vs' : '/static/cli/src/assets/monaco/dev/vs';
    if (typeof (<any>window).monaco === 'object') {
      this.finishLoadingMonaco();
      return;
    }
    const onGotAmdLoader: any = () => {
      // load Monaco
      (<any>window).require.config({ paths: { vs: `${baseUrl}` } });
      (<any>window).require([ `vs/editor/editor.main` ], () => {
        this.finishLoadingMonaco();
      });
    };

    // load AMD loader, if necessary
    if (!(<any>window).require) {
      const loaderScript: HTMLScriptElement = document.createElement('script');
      loaderScript.type = 'text/javascript';
      loaderScript.src = `${baseUrl}/loader.js`;
      loaderScript.addEventListener('load', onGotAmdLoader);
      document.body.appendChild(loaderScript);
    } else {
      onGotAmdLoader();
    }
  }

  public isMonacoAvailable(): boolean {
    return this.monacoLoaded;
  }

  public syncMonacoThemeToSohoTheme(): void {
    const currentTheme = this.lmThemeService.getCurrentThemeId();
    const monacoTheme = ('theme-new-dark' === currentTheme) ? 'vs-dark' : 'vs';
    this.addLPLLanguageToMonaco();
    monaco.editor.setTheme(monacoTheme);
  }

  public createValidationOptions(lplValSpec: LMLplValSpec, data: LMModel): LMEditorValidationOptions {
    if (lplValSpec !== undefined) {
      const busClassName    = data.getData().busClassName;
      const busViewFld      = lplValSpec.busViewFldId ? data.getField(lplValSpec.busViewFldId) : undefined;
      const specNameFld     = lplValSpec.specNameFldId ? data.getField(lplValSpec.specNameFldId) : undefined;
      const independentFld  = lplValSpec.independentFldId ? data.getField(lplValSpec.independentFldId) : undefined;

      let specType: string;
      if (lplValSpec.cfgEntryType) {
        specType = lplValSpec.cfgEntryType;
      } else {
        const configTypeFld = data.getField(lplValSpec.cfgEntryTypeFldId);
        specType = configTypeFld ? configTypeFld.value : undefined;

      }
      const type = this.convertValidationType(specType);
      const typeName = specNameFld ? specNameFld.value : undefined;
      const busViewName = busViewFld ? busViewFld.value : undefined;
      const specName = this.convertValidationSpecName(type, busViewName, typeName);

      if (type !== undefined && specName !== undefined) {
        const independent = independentFld ? independentFld.value : false;
        return {
          businessClass: busClassName,
          type,
          specName,
          independent
        };
      }
    }
    // if we got this far then there isn't options!
    return undefined;
  }

  private convertValidationType(type: string): LPLType {

    // map to the CFGEntry Class
    switch (type) {
      case 'BUSINESSCLASS':       return 'businessClass';
      case 'RELATION':            return 'relation';
      case 'COLUMNARRELATION':    return 'columnarRelation';
      case 'KEYFIELD':            return 'keyField';
      case 'FIELD':               return 'field';
      case 'ACTION':              return 'requestAction';
      case 'ACTIONCONFIGURATION': return 'actionConfiguration';
      case 'ACTION_EXTENSION':    return 'actionExtension';
      case 'USER_ACTION':         return 'userAction' ;
      case 'CUBE_CFG':            return 'cube';
      case 'LIST':                return 'list';
      case 'FORM':                return 'form';
      case 'MENU':                return 'menu';
      case 'PAGE':                return 'page';
      case 'RPT':                 return 'rptMap';
      case 'SECURITY_CLASS':      return 'secClass';
      case 'CARDVIEW':            return 'cardView';
      case 'WEBAPP':              return 'webApp';
      case 'WSI':                 return 'wsi';
      case 'PRODLINEOVERRIDE':    return 'prodLine';
      case 'NAVIGATION':          return 'navigation';
      case 'DRILLLIST':           return 'drillList';

      default: return undefined;
    }
  }

  private convertValidationSpecName(lplType: LPLType, busViewName: string, specName: string): string {
    if (lplType === undefined) {
      return undefined;
    }
    switch (lplType) {
      case 'menu':
      case 'page':
      case 'webApp':
      case 'rptMap':
      case 'secClass':
      case 'prodLine':
        return specName;
      case 'keyField':
      case 'businessClass':
      case 'list':
      case 'form':
      case 'cardView':
      case 'field':
      case 'relation':
      case 'columnarRelation':
      case 'userAction':
      case 'actionExtension':
      case 'actionConfiguration':
      case 'requestAction':
      case 'cube':
      case 'navigation':
      case 'drillList':
        return busViewName;
      default:
        return undefined;
    }
  }

  public canValidateLpl(options: LMEditorValidationOptions): boolean {
    if (!options) {
      return false;
    } else {
      switch (options.type) {
        case 'menu':
        case 'page':
        case 'list':
        case 'form':
        case 'cardView':
        case 'field':
        case 'relation':
        case 'columnarRelation':
        case 'actionExtension':
        case 'actionConfiguration':
        case 'requestAction':
        case 'webApp':
        case 'rptMap':
        case 'secClass':
        case 'keyField':
        case 'businessClass':
        case 'cube':
        case 'prodLine':
        case 'navigation':
        case 'drillList':
          return true;
        default:
          return false;
      }
    }
  }

  public validateLpl(changedLPL: string, options: LMEditorValidationOptions): Observable<LPLValidationResponse> {
    const businessClass = options.businessClass;
    const specName = options.specName;
    const independent = options.independent;

    switch (options.type) {
      case 'menu':
        return this.validateMenu(businessClass, specName, changedLPL, independent);
      case 'page':
        return this.validatePage(businessClass, specName, changedLPL);
      case 'list':
        return this.validateList(businessClass, specName, changedLPL, independent);
      case 'form':
        return this.validateForm(businessClass, specName, changedLPL, independent);
      case 'cardView':
        return this.validateCardView(businessClass, specName, changedLPL, independent);
      case 'field':
        return this.validateField(businessClass, specName, changedLPL);
      case 'relation':
        return this.validateRelation(businessClass, specName, changedLPL);
      case 'columnarRelation':
        return this.validateColumnarRelation(businessClass, specName, changedLPL);
      case 'userAction':
        return this.validateUserAction(businessClass, specName, changedLPL);
      case 'actionExtension':
        return this.validateActionExtension(businessClass, specName, changedLPL);
      case 'actionConfiguration':
        return this.validateActionConfiguration(businessClass, specName, changedLPL);
      case 'requestAction':
        return this.validateRequestAction(businessClass, specName, changedLPL);
      case 'webApp':
        return this.validateWebApp(businessClass, specName, changedLPL);
      case 'rptMap':
        return this.validateRptMap(businessClass, specName, changedLPL);
      case 'secClass':
        return this.validateSecurityClass(businessClass, specName, changedLPL);
      case 'keyField':
        return this.validateKeyField(businessClass, specName, changedLPL);
      case 'businessClass':
        return this.validateBusinessClass(businessClass, specName, changedLPL);
      case 'cube':
        return this.validateCube(businessClass, specName, changedLPL);
      case 'prodLine':
        return this.validateProdLine(businessClass, specName, changedLPL);
      case 'navigation':
        return this.validateNavigation(businessClass, specName, changedLPL);
      case 'drillList':
        return this.validateDrillList(businessClass, specName, changedLPL);

      default:
        return of(<LPLValidationResponse>{});
    }
  }

  public validateKeyField(businessClass: string, lplBusinessViewName: string, lpl: string): Observable<LPLValidationResponse> {
    const requestInfo = this.buildRequestInfo('keyField', lplBusinessViewName, lpl);
    return this.makeRequest(businessClass, requestInfo);
  }

  public validateBusinessClass(businessClass: string, lplBusinessViewName: string, lpl: string): Observable<LPLValidationResponse> {
    const requestInfo = this.buildRequestInfo('businessClass', lplBusinessViewName, lpl);
    return this.makeRequest(businessClass, requestInfo);
  }

  public validateRequestAction(businessClass: string, lplBusinessViewName: string, lpl: string): Observable<LPLValidationResponse> {
    const requestInfo = this.buildRequestInfo('requestAction', lplBusinessViewName, lpl);
    return this.makeRequest(businessClass, requestInfo);
  }

  public validateActionConfiguration(businessClass: string, lplBusinessViewName: string, lpl: string): Observable<LPLValidationResponse> {
    const requestInfo = this.buildRequestInfo('actionConfiguration', lplBusinessViewName, lpl);
    return this.makeRequest(businessClass, requestInfo);
  }

  public validateActionExtension(businessClass: string, lplBusinessViewName: string, lpl: string): Observable<LPLValidationResponse> {
    const requestInfo = this.buildRequestInfo('actionExtension', lplBusinessViewName, lpl);
    return this.makeRequest(businessClass, requestInfo);
  }

  public validateUserAction(businessClass: string, lplBusinessViewName: string, lpl: string): Observable<LPLValidationResponse> {
    const requestInfo = this.buildRequestInfo('userAction', lplBusinessViewName, lpl);
    return this.makeRequest(businessClass, requestInfo);
  }

  public validateField(businessClass: string, lplBusinessViewName: string, lpl: string): Observable<LPLValidationResponse> {
    const requestInfo = this.buildRequestInfo('field', lplBusinessViewName, lpl);
    return this.makeRequest(businessClass, requestInfo);
  }

  public validateRelation(businessClass: string, lplBusinessViewName: string, lpl: string): Observable<LPLValidationResponse> {
    const requestInfo = this.buildRequestInfo('relation', lplBusinessViewName, lpl);
    return this.makeRequest(businessClass, requestInfo);
  }
  
   public validateColumnarRelation(businessClass: string, lplBusinessViewName: string, lpl: string): Observable<LPLValidationResponse> {
    const requestInfo = this.buildRequestInfo('columnarRelation', lplBusinessViewName, lpl);
    return this.makeRequest(businessClass, requestInfo);
  }

  public validateWebApp(businessClass: string, lplWebAppName: string, lpl: string): Observable<LPLValidationResponse> {
    const requestInfo = this.buildRequestInfo('webApp', lplWebAppName, lpl);
    return this.makeRequest(businessClass, requestInfo);
  }

  public validateCube(businessClass: string, lplBusinessViewName: string, lpl: string): Observable<LPLValidationResponse> {
    const requestInfo = this.buildRequestInfo('cube', lplBusinessViewName, lpl);
    return this.makeRequest(businessClass, requestInfo);
  }

  public validateProdLine(businessClass: string, lplBusinessViewName: string, lpl: string): Observable<LPLValidationResponse> {
    const requestInfo = this.buildRequestInfo('prodLine', lplBusinessViewName, lpl);
    return this.makeRequest(businessClass, requestInfo);
  }

  public validateList(businessClass: string, lplBusinessViewName: string, lpl: string, independent?: boolean): Observable<LPLValidationResponse> {
    const requestInfo = this.buildRequestInfo('list', lplBusinessViewName, lpl);

    if (independent) {
      requestInfo.independent = true;
    }

    return this.makeRequest(businessClass, requestInfo);
  }

  public validateForm(businessClass: string, lplBusinessViewName: string, lpl: string, independent?: boolean): Observable<LPLValidationResponse> {
    const requestInfo = this.buildRequestInfo('form', lplBusinessViewName, lpl);

    if (independent) {
      requestInfo.independent = true;
    }

    return this.makeRequest(businessClass, requestInfo);
  }

  public validateMenu(businessClass: string, lplMenuName: string, lpl: string, independent?: boolean): Observable<LPLValidationResponse> {
    const requestInfo = this.buildRequestInfo('menu', lplMenuName, lpl);

    if (independent) {
      requestInfo.independent = true;
    }
    return this.makeRequest(businessClass, requestInfo);
  }

  public validateDrillList(businessClass: string, lplBusinessViewName: string, lpl: string, independent?: boolean): Observable<LPLValidationResponse> {
      const requestInfo = this.buildRequestInfo('drillList', lplBusinessViewName, lpl);

      if (independent) {
        requestInfo.independent = true;
      }

      return this.makeRequest(businessClass, requestInfo);
    }

  public validatePage(businessClass: string, lplPageName: string, lpl: string): Observable<LPLValidationResponse> {
    const requestInfo = this.buildRequestInfo('page', lplPageName, lpl);
    return this.makeRequest(businessClass, requestInfo);
  }

  public validateCardView(businessClass: string, lplBusinessViewName: string, lpl: string, independent?: boolean): Observable<LPLValidationResponse> {
    const requestInfo = this.buildRequestInfo('cardView', lplBusinessViewName, lpl);

    if (independent) {
      requestInfo.independent = true;
    }

    return this.makeRequest(businessClass, requestInfo);
  }

  public validateNavigation(businessClass: string, lplBusinessViewName: string, lpl: string, independent?: boolean): Observable<LPLValidationResponse> {
    const requestInfo = this.buildRequestInfo('navigation', lplBusinessViewName, lpl);

    if (independent) {
      requestInfo.independent = true;
    }

    return this.makeRequest(businessClass, requestInfo);
  }

  public validateSecurityClass(businessClass: string, secClassName: string, lpl: string): Observable<LPLValidationResponse> {
    const requestInfo = this.buildRequestInfo('secClass', secClassName, lpl);
    return this.makeRequest(businessClass, requestInfo);
  }

  public validateRptMap(businessClass: string, lplBusinessViewName: string, lpl: string): Observable<LPLValidationResponse> {
    const requestInfo = this.buildRequestInfo('rptMap', lplBusinessViewName, lpl);
    return this.makeRequest(businessClass, requestInfo);
  }

  public validateWebServiceInterface(businessClass: string, lplBusinessViewName: string, lpl: string, independent?: boolean): Observable<LPLValidationResponse> {
    const requestInfo = this.buildRequestInfo('wsi', lplBusinessViewName, lpl);

    if (independent) {
      requestInfo.independent = true;
    }

    return this.makeRequest(businessClass, requestInfo);
  }

  public updateLPLValidation(model: ITextModel, lplValidationResponse?: LPLValidationResponse, alert?: SohoAlertDirective): void {
    if (lplValidationResponse && lplValidationResponse.message) {
      this.setLPLMarkings(model, lplValidationResponse, alert);
    } else {
      this.clearLPLMarkings(model, alert);
    }
  }

  private setLPLMarkings(model: ITextModel, lplValidationResponse: LPLValidationResponse, alert?: SohoAlertDirective): void {

    if (lplValidationResponse.parserInfo && lplValidationResponse.parserInfo.length) {
      let hasError = false;
      lplValidationResponse.parserInfo.forEach(info => {
        const message = info ? info.message : lplValidationResponse.message;
        const alertType = info && info.severity === 'warning' ? 'info' : 'error';

        if (alert && alertType === 'error') {
          hasError = true;
          const alertMessage = this.htmlEntityEncode(message);
          alert.id = 'lpl-editor'; // need to make unique!
          alert.addInlineMessage(alertMessage, alertType, true);
        }

        const markers: IMarkerData[] = this.convertValidationResponse(lplValidationResponse);
        monaco.editor.setModelMarkers(model, 'LPL', markers);
      });

      if (!hasError) {
        alert.removeAllMessages();
      }
    } else if (lplValidationResponse.message) {
      // should never happen
      const errorMessage = this.htmlEntityEncode(lplValidationResponse.message);
      if (alert) {
        alert.id = 'lpl-editor'; // need to make unique!
        alert.addInlineMessage(errorMessage, 'error', true);
      }
    }
  }

  private clearLPLMarkings(model: ITextModel, alert?: SohoAlertDirective): void {
    if (alert) {
      alert.removeAllMessages();
    }

    monaco.editor.setModelMarkers(model, 'LPL', []);
  }

  private buildRequestInfo(listType: LPLType, specName: string, lpl: string): LPLValidationRequest {
    const requestInfo: LPLValidationRequest = {
      requestType: LMLplEditorService.LPL_VALIDATION_REQUEST,
      type: listType,
      specName,
      lpl
    };

    return requestInfo;
  }

  private buildRequestUrl(businessClass: string): string {
    const baseUrl = this.lmUrlUtilService.getBaseUrl();
    const url = baseUrl + /form/ + businessClass;
    return url;
  }

  private makeRequest(businessClass: string, post: LPLValidationRequest): Observable<LPLValidationResponse> {
    const requestUrl = this.buildRequestUrl(businessClass);

    const headers = new HttpHeaders();
    headers.append('Content-Type', 'application/json;charset=utf-8');
    return <Observable<LPLValidationResponse>>this.httpClient.post(requestUrl, JSON.stringify(post), { headers }).pipe(
      catchError(error => throwError(error || 'Server error')));
  }

  // Utility methods would be nice to put this somewhere else

  public convertValidationResponse(lplValidationResponse: LPLValidationResponse): IMarkerData[] {
    const markers: IMarkerData[] = new Array<IMarkerData>();

    if (lplValidationResponse.parserInfo && lplValidationResponse.parserInfo.length) {

      lplValidationResponse.parserInfo.forEach(info => {
        const alertType = info.severity === 'warning' ? 'info' : 'error';
        markers.push({
          startLineNumber: info.row,
          startColumn:     info.start,
          endLineNumber:   info.row,
          endColumn:       info.end,
          message:         info.message,
          severity:        alertType === 'error' ? monaco.MarkerSeverity.Error : monaco.MarkerSeverity.Warning
        }
        );
      });
    }

    return markers;
  }

  public htmlEntityEncode(text: string): string {
    let encodedText = text;
    encodedText = encodedText.replace(/&/g, '&amp;');
    encodedText = encodedText.replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return encodedText;
  }

  public htmlEntityDecode(encodedText: string): string {
    let text = encodedText;
    text = text.replace(/&lt;/g, '<').replace(/&gt;/g, '>');
    text = text.replace(/&amp;/g, '&');
    return text;
  }

  LPL_LANGUAGE_CONFIGURATION: monaco.languages.LanguageConfiguration = {
    // the default separators except `@$`
    wordPattern: /(-?\d*\.\d\w*)|([^\`\~\!\#\%\^\&\*\(\)\-\=\+\[\{\]\}\\\|\;\:\'\"\,\.\<\>\/\?\s]+)/g,
    comments: {
      lineComment: '//',
      blockComment: [ '/*', '*/' ],
    },
    brackets: [
      [ '{', '}' ],
      [ '[', ']' ],
      [ '(', ')' ],
    ],
    autoClosingPairs: [
      { open: '{', close: '}' },
      { open: '[', close: ']' },
      { open: '(', close: ')' },
      { open: '"', close: '"' },
      { open: '\'', close: '\'' },
    ],
    surroundingPairs: [
      { open: '{', close: '}' },
      { open: '[', close: ']' },
      { open: '(', close: ')' },
      { open: '"', close: '"' },
      { open: '\'', close: '\'' },
      { open: '<', close: '>' },
    ],
    folding: {
      markers: {
        start: new RegExp('^\\s*//\\s*(?:(?:#?region\\b)|(?:<editor-fold\\b))'),
        end: new RegExp('^\\s*//\\s*(?:(?:#?endregion\\b)|(?:</editor-fold>))')
      }
    }
  };

  public addLPLLanguageToMonaco(): void {
    if (this.isMonacoAvailable()) {
      const lplLanguage = monaco.languages.getLanguages().find(language => language.id === 'LPL');

      if (!lplLanguage) {
        monaco.languages.register({ id: 'LPL' });
        monaco.languages.setLanguageConfiguration('LPL', this.LPL_LANGUAGE_CONFIGURATION);
        monaco.languages.setMonarchTokensProvider('LPL', this.LPL_LANGUAGE);
      }

      const secclassLanguage = monaco.languages.getLanguages().find(language => language.id === 'secclass');

      if (!secclassLanguage) {
        monaco.languages.register({ id: 'secclass' });
        monaco.languages.setLanguageConfiguration('secclass', this.LPL_LANGUAGE_CONFIGURATION);
        monaco.languages.setMonarchTokensProvider('secclass', this.SECCLASS_LANGUAGE);
        monaco.languages.registerCompletionItemProvider('secclass', {
          provideCompletionItems: (model, position, context, token) =>
            this.getSecClassCompletionList(model, position, context, token),
        });
      }
    }
  }

  LPL_LANGUAGE = <monaco.languages.IMonarchLanguage>{
    defaultToken: '',
    tokenPostfix: '.LPL',

    keywords: [
      'abstract', 'continue', 'for', 'new', 'switch', 'assert', 'default',
      'goto', 'package', 'synchronized', 'boolean', 'do', 'if', 'private',
      'this', 'break', 'double', 'implements', 'protected', 'throw', 'byte',
      'else', 'import', 'public', 'throws', 'case', 'enum', 'instanceof', 'return',
      'transient', 'catch', 'extends', 'int', 'short', 'try', 'char', 'final',
      'interface', 'static', 'void', 'class', 'finally', 'long', 'strictfp',
      'volatile', 'const', 'float', 'native', 'super', 'while', 'true', 'false',
    ],

    operators: [
      '=', '>', '<', '!', '~', '?', ':',
      '==', '<=', '>=', '!=', '&&', '||', '++', '--',
      '+', '-', '*', '/', '&', '|', '^', '%', '<<',
      '>>', '>>>', '+=', '-=', '*=', '/=', '&=', '|=',
      '^=', '%=', '<<=', '>>=', '>>>='
    ],

    // we include these common regular expressions
    symbols: /[=><!~?:&|+\-*\/\^%]+/,
    escapes: /\\(?:[abfnrtv\\"']|x[0-9A-Fa-f]{1,4}|u[0-9A-Fa-f]{4}|U[0-9A-Fa-f]{8})/,
    digits: /\d+(_+\d+)*/,
    octaldigits: /[0-7]+(_+[0-7]+)*/,
    binarydigits: /[0-1]+(_+[0-1]+)*/,
    hexdigits: /[[0-9a-fA-F]+(_+[0-9a-fA-F]+)*/,

    // The main tokenizer for our languages
    tokenizer: {
      root: [
        // landmark keywords
        [ /\bGroupField\b/, { token: 'keyword.$0' } ],
        [ /\bAlphaRight\b/, { token: 'keyword.$0' } ],
        [ /\bAlphaUpper\b/, { token: 'keyword.$0' } ],
        [ /\bAlpha\b/, { token: 'keyword.$0' } ],
        [ /\bAnniversary\b/, { token: 'keyword.$0' } ],
        [ /\bLPL\b/, { token: 'keyword.$0' } ],
        [ /\bBoolean\b/, { token: 'keyword.$0' } ],
        [ /\bUniqueID\b/, { token: 'keyword.$0' } ],
        [ /\bDate\b/, { token: 'keyword.$0' } ],
        [ /\bDecimal\b/, { token: 'keyword.$0' } ],
        [ /\bUnsigned Decimal\b/, { token: 'keyword.$0' } ],
        [ /\b_reserved_48\b/, { token: 'keyword.$0' } ],
        [ /\bIteration\b/, { token: 'keyword.$0' } ],
        [ /\bInteger\b/, { token: 'keyword.$0' } ],
        [ /\b_reserved_51\b/, { token: 'keyword.$0' } ],
        [ /\bNumeric\b/, { token: 'keyword.$0' } ],
        [ /\bPeriod\b/, { token: 'keyword.$0' } ],
        [ /\bPercent\b/, { token: 'keyword.$0' } ],
        [ /\bUnsigned Percent\b/, { token: 'keyword.$0' } ],
        [ /\bTime\b/, { token: 'keyword.$0' } ],
        [ /\bRichText\b/, { token: 'keyword.$0' } ],
        [ /\bYear\b/, { token: 'keyword.$0' } ],
        [ /\bTimeStamp\b/, { token: 'keyword.$0' } ],
        [ /\bBinaryDocument\b/, { token: 'keyword.$0' } ],
        [ /\bBinaryObject\b/, { token: 'keyword.$0' } ],
        [ /\bText\b/, { token: 'keyword.$0' } ],

        [ /\bnot indexed\b/, { token: 'keyword.$0' } ],
        [ /\bAccountingTransaction\b/, { token: 'keyword.$0' } ],
        [ /\bAccumulators\b/, { token: 'keyword.$0' } ],
        [ /\bAction Rules\b/, { token: 'keyword.$0' } ],
        [ /\bAction\b/, { token: 'keyword.$0' } ],
        [ /\bActions\b/, { token: 'keyword.$0' } ],
        [ /\bAgent\b/, { token: 'keyword.$0' } ],
        [ /\bAggregate By\b/, { token: 'keyword.$0' } ],
        [ /\bAggregate For\b/, { token: 'keyword.$0' } ],
        [ /\bAggregate Instances\b/, { token: 'keyword.$0' } ],
        [ /\bArrayValueField\b/, { token: 'keyword.$0' } ],
        [ /\bAttach Rules\b/, { token: 'keyword.$0' } ],
        [ /\bBusinessPolicy\b/, { token: 'keyword.$0' } ],
        [ /\bClass\b/, { token: 'keyword.$0' } ],
        [ /\bComputeField\b/, { token: 'keyword.$0' } ],
        [ /\bConditionalField\b/, { token: 'keyword.$0' } ],
        [ /\bConditions\b/, { token: 'keyword.$0' } ],
        [ /\bContext\b/, { token: 'keyword.$0' } ],
        [ /\bDerived Fields\b/, { token: 'keyword.$0' } ],
        [ /\bDisplay Fields\b/, { token: 'keyword.$0' } ],
        [ /\bDocumentDistribution\b/, { token: 'keyword.$0' } ],
        [ /\bDocumentFulfillment\b/, { token: 'keyword.$0' } ],
        [ /\bDocument\b/, { token: 'keyword.$0' } ],
        [ /\bbusclass\b/, { token: 'keyword.$0' } ],
        [ /\bbutton of\b/, { token: 'keyword.$0' } ],
        [ /\bhelper list is\b/, { token: 'keyword.$0' } ],
        [ /\bDynamic Creation Rules\b/, { token: 'keyword.$0' } ],
        [ /\bto nearest\b/, { token: 'keyword.$0' } ],
        [ /\bEntityComponent\b/, { token: 'keyword.$0' } ],
        [ /\bEntity\b/, { token: 'keyword.$0' } ],
        [ /\bField Mapping\b/, { token: 'keyword.$0' } ],
        [ /\bField Rules\b/, { token: 'keyword.$0' } ],
        [ /\bField\b/, { token: 'keyword.$0' } ],
        [ /\bFields\b/, { token: 'keyword.$0' } ],
        [ /\bGlobal Rules\b/, { token: 'keyword.$0' } ],
        [ /\bGroup Fields\b/, { token: 'keyword.$0' } ],
        [ /\bImplements Modeling Pattern\b/, { token: 'keyword.$0' } ],
        [ /\bcurrent period\b/, { token: 'keyword.$0' } ],
        [ /\bInstance Rules\b/, { token: 'keyword.$0' } ],
        [ /\bInstance Selection\b/, { token: 'keyword.$0' } ],
        [ /\bKeyField\b/, { token: 'keyword.$0' } ],
        [ /\bLocation\b/, { token: 'keyword.$0' } ],
        [ /\bNativeField\b/, { token: 'keyword.$0' } ],
        [ /\bon\b/, { token: 'keyword.$0' } ],
        [ /\bOntology\b/, { token: 'keyword.$0' } ],
        [ /\bround\b/, { token: 'keyword.$0' } ],
        [ /\bParameters\b/, { token: 'keyword.$0' } ],
        [ /\bPatterns\b/, { token: 'keyword.$0' } ],
        [ /\bPersistent Fields\b/, { token: 'keyword.$0' } ],
        [ /\bLocal Fields\b/, { token: 'keyword.$0' } ],
        [ /\bMessageField\b/, { token: 'keyword.$0' } ],
        [ /\bRelations\b/, { token: 'keyword.$0' } ],
        [ /\brelative key is\b/, { token: 'keyword.$0' } ],
        [ /\bRepresentation\b/, { token: 'keyword.$0' } ],
        [ /\bRules\b/, { token: 'keyword.$0' } ],
        [ /\bSet Is\b/, { token: 'keyword.$0' } ],
        [ /\bSet Rules\b/, { token: 'keyword.$0' } ],
        [ /\bSets\b/, { token: 'keyword.$0' } ],
        [ /\bSort Order\b/, { token: 'keyword.$0' } ],
        [ /\bState Rules\b/, { token: 'keyword.$0' } ],
        [ /\bStateCycles\b/, { token: 'keyword.$0' } ],
        [ /\bState\b/, { token: 'keyword.$0' } ],
        [ /\bStates\b/, { token: 'keyword.$0' } ],
        [ /\bStringField\b/, { token: 'keyword.$0' } ],
        [ /\bcurrent anniversary\b/, { token: 'keyword.$0' } ],
        [ /\bSubType\b/, { token: 'keyword.$0' } ],
        [ /\bSurrogate For\b/, { token: 'keyword.$0' } ],
        [ /\bTransient Fields\b/, { token: 'keyword.$0' } ],
        [ /\badd\b/, { token: 'keyword.$0' } ],
        [ /\baggregate continuously\b/, { token: 'keyword.$0' } ],
        [ /\baggregate of\b/, { token: 'keyword.$0' } ],
        [ /\baggregate when needed\b/, { token: 'keyword.$0' } ],
        [ /\balternate key is\b/, { token: 'keyword.$0' } ],
        [ /\balways default as a group\b/, { token: 'keyword.$0' } ],
        [ /\balways default individual fields\b/, { token: 'keyword.$0' } ],
        [ /\band\b/, { token: 'keyword.$0' } ],
        [ /\bany\b/, { token: 'keyword.$0' } ],
        [ /\barchive of\b/, { token: 'keyword.$0' } ],
        [ /\barray size is\b/, { token: 'keyword.$0' } ],
        [ /\bas of\b/, { token: 'keyword.$0' } ],
        [ /\bas\b/, { token: 'keyword.$0' } ],
        [ /\bautosequence\b/, { token: 'keyword.$0' } ],
        [ /\bautosequenced by database\b/, { token: 'keyword.$0' } ],
        [ /\bblank\b/, { token: 'keyword.$0' } ],
        [ /\bbusiness class is\b/, { token: 'keyword.$0' } ],
        [ /\bcannot be entered\b/, { token: 'keyword.$0' } ],
        [ /\bceiling\b/, { token: 'keyword.$0' } ],
        [ /\bchanged\b/, { token: 'keyword.$0' } ],
        [ /\bclassic name is\b/, { token: 'keyword.$0' } ],
        [ /\bcondition\b/, { token: 'keyword.$0' } ],
        [ /\bconnect via UniqueID\b/, { token: 'keyword.$0' } ],
        [ /\bconstraint\b/, { token: 'keyword.$0' } ],
        [ /\bcurrent year\b/, { token: 'keyword.$0' } ],
        [ /\bedit contiguous\b/, { token: 'keyword.$0' } ],
        [ /\bCommit Rules\b/, { token: 'keyword.$0' } ],
        [ /\bcurrent date\b/, { token: 'keyword.$0' } ],
        [ /\bcurrent time\b/, { token: 'keyword.$0' } ],
        [ /\bedit decreasing sequence\b/, { token: 'keyword.$0' } ],
        [ /\bdefault as a group\b/, { token: 'keyword.$0' } ],
        [ /\bdefault individual fields\b/, { token: 'keyword.$0' } ],
        [ /\bdefault to\b/, { token: 'keyword.$0' } ],
        [ /\bdelete cascades\b/, { token: 'keyword.$0' } ],
        [ /\bdelete ignored\b/, { token: 'keyword.$0' } ],
        [ /\bdelete restricted\b/, { token: 'keyword.$0' } ],
        [ /\bdescending\b/, { token: 'keyword.$0' } ],
        [ /\bdisable surrogate context\b/, { token: 'keyword.$0' } ],
        [ /\bduplicates\b/, { token: 'keyword.$0' } ],
        [ /\bdynamic default to\b/, { token: 'keyword.$0' } ],
        [ /\beach\b/, { token: 'keyword.$0' } ],
        [ /\beffective date as a group\b/, { token: 'keyword.$0' } ],
        [ /\belse\b/, { token: 'keyword.$0' } ],
        [ /\benable surrogate context\b/, { token: 'keyword.$0' } ],
        [ /\bentered\b/, { token: 'keyword.$0' } ],
        [ /\bexist\b/, { token: 'keyword.$0' } ],
        [ /\bexists\b/, { token: 'keyword.$0' } ],
        [ /\bextends\b/, { token: 'keyword.$0' } ],
        [ /\bfalse\b/, { token: 'keyword.$0' } ],
        [ /\bfield size is\b/, { token: 'keyword.$0' } ],
        [ /\buser fields\b/, { token: 'keyword.$0' } ],
        [ /\bfirst\b/, { token: 'keyword.$0' } ],
        [ /\bfixed\b/, { token: 'keyword.$0' } ],
        [ /\bfloor\b/, { token: 'keyword.$0' } ],
        [ /\bfor each\b/, { token: 'keyword.$0' } ],
        [ /\bforce default to\b/, { token: 'keyword.$0' } ],
        [ /\bfrom\b/, { token: 'keyword.$0' } ],
        [ /\bgroupfield\b/, { token: 'keyword.$0' } ],
        [ /\bif\b/, { token: 'keyword.$0' } ],
        [ /\bcannot be changed\b/, { token: 'keyword.$0' } ],
        [ /\bimplements Attachments\b/, { token: 'keyword.$0' } ],
        [ /\bimplements CRUD\b/, { token: 'keyword.$0' } ],
        [ /\bimplements DynamicCreation\b/, { token: 'keyword.$0' } ],
        [ /\bimplements EffectiveDated\b/, { token: 'keyword.$0' } ],
        [ /\bimplements ManualCode\b/, { token: 'keyword.$0' } ],
        [ /\bimplements Specialization\b/, { token: 'keyword.$0' } ],
        [ /\bimplements UniqueID\b/, { token: 'keyword.$0' } ],
        [ /\bimplements WorkFile\b/, { token: 'keyword.$0' } ],
        [ /\bin context\b/, { token: 'keyword.$0' } ],
        [ /\bedit increasing sequence\b/, { token: 'keyword.$0' } ],
        [ /\bindex of\b/, { token: 'keyword.$0' } ],
        [ /\bindexed\b/, { token: 'keyword.$0' } ],
        [ /\binitial state is\b/, { token: 'keyword.$0' } ],
        [ /\binitial state\b/, { token: 'keyword.$0' } ],
        [ /\binitialize\b/, { token: 'keyword.$0' } ],
        [ /\binvoke\b/, { token: 'keyword.$0' } ],
        [ /\bBusinessClass\b/, { token: 'keyword.$0' } ],
        [ /\bis a Create Action\b/, { token: 'keyword.$0' } ],
        [ /\bis a Delete Action\b/, { token: 'keyword.$0' } ],
        [ /\bimplements ReadOnly\b/, { token: 'keyword.$0' } ],
        [ /\bis a Preview Action\b/, { token: 'keyword.$0' } ],
        [ /\bis a Set Action\b/, { token: 'keyword.$0' } ],
        [ /\bis a StateCycle\b/, { token: 'keyword.$0' } ],
        [ /\bis a State\b/, { token: 'keyword.$0' } ],
        [ /\bis a replica of\b/, { token: 'keyword.$0' } ],
        [ /\bis a snapshot of\b/, { token: 'keyword.$0' } ],
        [ /\bis a\b/, { token: 'keyword.$0' } ],
        [ /\bis an Action\b/, { token: 'keyword.$0' } ],
        [ /\bis an Update Action\b/, { token: 'keyword.$0' } ],
        [ /\bis an\b/, { token: 'keyword.$0' } ],
        [ /\bis high value\b/, { token: 'keyword.$0' } ],
        [ /\bis old\b/, { token: 'keyword.$0' } ],
        [ /\bis the aggregate of\b/, { token: 'keyword.$0' } ],
        [ /\bis the instance count of\b/, { token: 'keyword.$0' } ],
        [ /\bis\b/, { token: 'keyword.$0' } ],
        [ /\bjava name is\b/, { token: 'keyword.$0' } ],
        [ /\bkey change not allowed\b/, { token: 'keyword.$0' } ],
        [ /\bkey fields\b/, { token: 'keyword.$0' } ],
        [ /\blast\b/, { token: 'keyword.$0' } ],
        [ /\bmake contiguous\b/, { token: 'keyword.$0' } ],
        [ /\bmake decreasing sequence\b/, { token: 'keyword.$0' } ],
        [ /\bmake increasing sequence\b/, { token: 'keyword.$0' } ],
        [ /\bmake transition to\b/, { token: 'keyword.$0' } ],
        [ /\bmodeled state is\b/, { token: 'keyword.$0' } ],
        [ /\bname is\b/, { token: 'keyword.$0' } ],
        [ /\bno duplicates\b/, { token: 'keyword.$0' } ],
        [ /\bno implementation\b/, { token: 'keyword.$0' } ],
        [ /\bno\b/, { token: 'keyword.$0' } ],
        [ /\bis an Instance Action\b/, { token: 'keyword.$0' } ],
        [ /\bnot used\b/, { token: 'keyword.$0' } ],
        [ /\bnot\b/, { token: 'keyword.$0' } ],
        [ /\bmust be numeric\b/, { token: 'keyword.$0' } ],
        [ /\boccurs\b/, { token: 'keyword.$0' } ],
        [ /\bold\b/, { token: 'keyword.$0' } ],
        [ /\bone-to-many relation\b/, { token: 'keyword.$0' } ],
        [ /\bone-to-one relation\b/, { token: 'keyword.$0' } ],
        [ /\bonly\b/, { token: 'keyword.$0' } ],
        [ /\boptional\b/, { token: 'keyword.$0' } ],
        [ /\bor\b/, { token: 'keyword.$0' } ],
        [ /\bowned by\b/, { token: 'keyword.$0' } ],
        [ /\bpart of\b/, { token: 'keyword.$0' } ],
        [ /\bprefix is\b/, { token: 'keyword.$0' } ],
        [ /\bprimary\b/, { token: 'keyword.$0' } ],
        [ /\brecalculate\b/, { token: 'keyword.$0' } ],
        [ /\brequired\b/, { token: 'keyword.$0' } ],
        [ /\breset action is\b/, { token: 'keyword.$0' } ],
        [ /\bset retrieval is inclusive\b/, { token: 'keyword.$0' } ],
        [ /\bset\b/, { token: 'keyword.$0' } ],
        [ /\bsize\b/, { token: 'keyword.$0' } ],
        [ /\bsizeofarray\b/, { token: 'keyword.$0' } ],
        [ /\bsql name is\b/, { token: 'keyword.$0' } ],
        [ /\bstate field =\b/, { token: 'keyword.$0' } ],
        [ /\bstate field is\b/, { token: 'keyword.$0' } ],
        [ /\bstatefield\b/, { token: 'keyword.$0' } ],
        [ /\bstereotype is\b/, { token: 'keyword.$0' } ],
        [ /\bsubtract\b/, { token: 'keyword.$0' } ],
        [ /\bsum the calculation\b/, { token: 'keyword.$0' } ],
        [ /\bsum\b/, { token: 'keyword.$0' } ],
        [ /\bsymbolic key is\b/, { token: 'keyword.$0' } ],
        [ /\btimes\b/, { token: 'keyword.$0' } ],
        [ /\bto\b/, { token: 'keyword.$0' } ],
        [ /\btrue\b/, { token: 'keyword.$0' } ],
        [ /\bup to nearest\b/, { token: 'keyword.$0' } ],
        [ /\bdown to nearest\b/, { token: 'keyword.$0' } ],
        [ /\btype is\b/, { token: 'keyword.$0' } ],
        [ /\bunmodeled state is\b/, { token: 'keyword.$0' } ],
        [ /\bup to\b/, { token: 'keyword.$0' } ],
        [ /\bupdate\b/, { token: 'keyword.$0' } ],
        [ /\buses\b/, { token: 'keyword.$0' } ],
        [ /\busing\b/, { token: 'keyword.$0' } ],
        [ /\bvalid when\b/, { token: 'keyword.$0' } ],
        [ /\bvalue is\b/, { token: 'keyword.$0' } ],
        [ /\bconfirmation required\b/, { token: 'keyword.$0' } ],
        [ /\bwhen\b/, { token: 'keyword.$0' } ],
        [ /\bwhere\b/, { token: 'keyword.$0' } ],
        [ /\ball\b/, { token: 'keyword.$0' } ],
        [ /\bmin\b/, { token: 'keyword.$0' } ],
        [ /\bmax\b/, { token: 'keyword.$0' } ],
        [ /\bavg\b/, { token: 'keyword.$0' } ],
        [ /\bimplements TemplateDriven by\b/, { token: 'keyword.$0' } ],
        [ /\bimplements Auditing\b/, { token: 'keyword.$0' } ],
        [ /\btext searchable\b/, { token: 'keyword.$0' } ],
        [ /\binstance count of\b/, { token: 'keyword.$0' } ],
        [ /\bis numeric\b/, { token: 'keyword.$0' } ],
        [ /\bmutually exclusive\b/, { token: 'keyword.$0' } ],
        [ /\bsql prefix is\b/, { token: 'keyword.$0' } ],
        [ /\b<<<<\b/, { token: 'keyword.$0' } ],
        [ /\b====\b/, { token: 'keyword.$0' } ],
        [ /\b>>>>\b/, { token: 'keyword.$0' } ],
        [ /\bimplements CreateStamp\b/, { token: 'keyword.$0' } ],
        [ /\bimplements UpdateStamp\b/, { token: 'keyword.$0' } ],
        [ /\blike\b/, { token: 'keyword.$0' } ],
        [ /\bon entrance to\b/, { token: 'keyword.$0' } ],
        [ /\bpart of key\b/, { token: 'keyword.$0' } ],
        [ /\bsymbolic key\b/, { token: 'keyword.$0' } ],
        [ /\bParameter Rules\b/, { token: 'keyword.$0' } ],
        [ /\bdefault\b/, { token: 'keyword.$0' } ],
        [ /\bset to\b/, { token: 'keyword.$0' } ],
        [ /\brollback changes to\b/, { token: 'keyword.$0' } ],
        [ /\bis a ListView\b/, { token: 'keyword.$0' } ],
        [ /\bvisible when\b/, { token: 'keyword.$0' } ],
        [ /\bfilter form is\b/, { token: 'keyword.$0' } ],
        [ /\bcurrent timestamp\b/, { token: 'keyword.$0' } ],
        [ /\bRelative Key\b/, { token: 'keyword.$0' } ],
        [ /\bon exit of\b/, { token: 'keyword.$0' } ],
        [ /\bcontrol valid key values\b/, { token: 'keyword.$0' } ],
        [ /\bimplements Validity\b/, { token: 'keyword.$0' } ],
        [ /\bContext Fields\b/, { token: 'keyword.$0' } ],
        [ /\bRule Blocks\b/, { token: 'keyword.$0' } ],
        [ /\binclude\b/, { token: 'keyword.$0' } ],
        [ /\bimplements UserDefinedStates\b/, { token: 'keyword.$0' } ],
        [ /\bno rollback\b/, { token: 'keyword.$0' } ],
        [ /\bDocumentAccounting\b/, { token: 'keyword.$0' } ],
        [ /\bnull\b/, { token: 'keyword.$0' } ],
        [ /\bimplements Proxy for\b/, { token: 'keyword.$0' } ],
        [ /\bimplements ContextualParent\b/, { token: 'keyword.$0' } ],
        [ /\bisbusclass\b/, { token: 'keyword.$0' } ],
        [ /\bname\b/, { token: 'keyword.$0' } ],
        [ /\bstereotype\b/, { token: 'keyword.$0' } ],
        [ /\bistransaction\b/, { token: 'keyword.$0' } ],
        [ /\bmodule\b/, { token: 'keyword.$0' } ],
        [ /\bisbustask\b/, { token: 'keyword.$0' } ],
        [ /\bparentcontext\b/, { token: 'keyword.$0' } ],
        [ /\bsymbolickey\b/, { token: 'keyword.$0' } ],
        [ /\binstancekey\b/, { token: 'keyword.$0' } ],
        [ /\bcontains\b/, { token: 'keyword.$0' } ],
        [ /\bimplements BusinessPolicyIntegrity\b/, { token: 'keyword.$0' } ],
        [ /\bBusinessTask\b/, { token: 'keyword.$0' } ],
        [ /\bResults\b/, { token: 'keyword.$0' } ],
        [ /\bview\b/, { token: 'keyword.$0' } ],
        [ /\bnot like\b/, { token: 'keyword.$0' } ],
        [ /\bassign result to\b/, { token: 'keyword.$0' } ],
        [ /\bField Groups\b/, { token: 'keyword.$0' } ],
        [ /\binvoked\b/, { token: 'keyword.$0' } ],
        [ /\brelated\b/, { token: 'keyword.$0' } ],
        [ /\bEntrance Rules\b/, { token: 'keyword.$0' } ],
        [ /\bExit Rules\b/, { token: 'keyword.$0' } ],
        [ /\bautomate context\b/, { token: 'keyword.$0' } ],
        [ /\bis a SecurityClass\b/, { token: 'keyword.$0' } ],
        [ /\bextends\b/, { token: 'keyword.$0' } ],
        [ /\bAccess Rights\b/, { token: 'keyword.$0' } ],
        [ /\bis accessible\b/, { token: 'keyword.$0' } ],
        [ /\bwhen\b/, { token: 'keyword.$0' } ],
        [ /\bunconditionally\b/, { token: 'keyword.$0' } ],
        [ /\bis not accessible\b/, { token: 'keyword.$0' } ],
        [ /\bKeyField\b/, { token: 'keyword.$0' } ],
        [ /\bBusinessClass\b/, { token: 'keyword.$0' } ],
        [ /\bBusinessTask\b/, { token: 'keyword.$0' } ],
        [ /\bExecutable\b/, { token: 'keyword.$0' } ],
        [ /\bForm\b/, { token: 'keyword.$0' } ],
        [ /\bPageDefinition\b/, { token: 'keyword.$0' } ],
        [ /\bModule\b/, { token: 'keyword.$0' } ],
        [ /\bProductLine\b/, { token: 'keyword.$0' } ],
        [ /\bDataArea\b/, { token: 'keyword.$0' } ],
        [ /\bField\b/, { token: 'keyword.$0' } ],
        [ /\ball actions\b/, { token: 'keyword.$0' } ],
        [ /\ball inquiries\b/, { token: 'keyword.$0' } ],
        [ /\ball updates\b/, { token: 'keyword.$0' } ],
        [ /\ball creates\b/, { token: 'keyword.$0' } ],
        [ /\ball deletes\b/, { token: 'keyword.$0' } ],
        [ /\ball functions\b/, { token: 'keyword.$0' } ],
        [ /\bgrants access\b/, { token: 'keyword.$0' } ],
        [ /\bis leap year\b/, { token: 'keyword.$0' } ],
        [ /\bdecimals\b/, { token: 'keyword.$0' } ],
        [ /\byear\b/, { token: 'keyword.$0' } ],
        [ /\bmonth\b/, { token: 'keyword.$0' } ],
        [ /\bday\b/, { token: 'keyword.$0' } ],
        [ /\bis default select\b/, { token: 'keyword.$0' } ],
        [ /\bmanual update\b/, { token: 'keyword.$0' } ],
        [ /\bis a Form\b/, { token: 'keyword.$0' } ],
        [ /\bis primary\b/, { token: 'keyword.$0' } ],
        [ /\bLayout\b/, { token: 'keyword.$0' } ],
        [ /\bheader1 of\b/, { token: 'keyword.$0' } ],
        [ /\bheader2 of\b/, { token: 'keyword.$0' } ],
        [ /\bheader3 of\b/, { token: 'keyword.$0' } ],
        [ /\bheader4 of\b/, { token: 'keyword.$0' } ],
        [ /\bheader5 of\b/, { token: 'keyword.$0' } ],
        [ /\bparagraph\b/, { token: 'keyword.$0' } ],
        [ /\bhorizontal\b/, { token: 'keyword.$0' } ],
        [ /\bvertical\b/, { token: 'keyword.$0' } ],
        [ /\bright align\b/, { token: 'keyword.$0' } ],
        [ /\bleft align\b/, { token: 'keyword.$0' } ],
        [ /\bcenter\b/, { token: 'keyword.$0' } ],
        [ /\bsingle column\b/, { token: 'keyword.$0' } ],
        [ /\btwo column\b/, { token: 'keyword.$0' } ],
        [ /\bthree column\b/, { token: 'keyword.$0' } ],
        [ /\bfour column\b/, { token: 'keyword.$0' } ],
        [ /\bfive column\b/, { token: 'keyword.$0' } ],
        [ /\blabel is\b/, { token: 'keyword.$0' } ],
        [ /\bDerivedField\b/, { token: 'keyword.$0' } ],
        [ /\btitle is\b/, { token: 'keyword.$0' } ],
        [ /\bfor\b/, { token: 'keyword.$0' } ],
        [ /\bno label\b/, { token: 'keyword.$0' } ],
        [ /\bis an ActionForm\b/, { token: 'keyword.$0' } ],
        [ /\bform is\b/, { token: 'keyword.$0' } ],
        [ /\bis a Navigation\b/, { token: 'keyword.$0' } ],
        [ /\bFilter By\b/, { token: 'keyword.$0' } ],
        [ /\blist is\b/, { token: 'keyword.$0' } ],
        [ /\bis a List\b/, { token: 'keyword.$0' } ],
        [ /\bpropagate as a Navigation\b/, { token: 'keyword.$0' } ],
        [ /\ball ontology\b/, { token: 'keyword.$0' } ],
        [ /\binitiate\b/, { token: 'keyword.$0' } ],
        [ /\bprocess\b/, { token: 'keyword.$0' } ],
        [ /\bCriteria\b/, { token: 'keyword.$0' } ],
        [ /\bcategory filter is\b/, { token: 'keyword.$0' } ],
        [ /\bVariables\b/, { token: 'keyword.$0' } ],
        [ /\bin\b/, { token: 'keyword.$0' } ],
        [ /\bout\b/, { token: 'keyword.$0' } ],
        [ /\bactor\b/, { token: 'keyword.$0' } ],
        [ /\bfilter by\b/, { token: 'keyword.$0' } ],
        [ /\bcolumn1\b/, { token: 'keyword.$0' } ],
        [ /\bcolumn2\b/, { token: 'keyword.$0' } ],
        [ /\bcolumn3\b/, { token: 'keyword.$0' } ],
        [ /\bcompact format\b/, { token: 'keyword.$0' } ],
        [ /\bimplements CompactFormat\b/, { token: 'keyword.$0' } ],
        [ /\bof\b/, { token: 'keyword.$0' } ],
        [ /\bpercent\b/, { token: 'keyword.$0' } ],
        [ /\bindent\b/, { token: 'keyword.$0' } ],
        [ /\bis a WizardForm\b/, { token: 'keyword.$0' } ],
        [ /\bis a CompositeForm\b/, { token: 'keyword.$0' } ],
        [ /\bis a Panel\b/, { token: 'keyword.$0' } ],
        [ /\brestricted\b/, { token: 'keyword.$0' } ],
        [ /\bmode\b/, { token: 'keyword.$0' } ],
        [ /\bthis instance\b/, { token: 'keyword.$0' } ],
        [ /\bexcept\b/, { token: 'keyword.$0' } ],
        [ /\bfill in fields from\b/, { token: 'keyword.$0' } ],
        [ /\bfill in blank fields from\b/, { token: 'keyword.$0' } ],
        [ /\blatest\b/, { token: 'keyword.$0' } ],
        [ /\ballow anonymous access\b/, { token: 'keyword.$0' } ],
        [ /\bimplements LazyUniqueID\b/, { token: 'keyword.$0' } ],
        [ /\bsql set name is\b/, { token: 'keyword.$0' } ],
        [ /\bdelimiter is\b/, { token: 'keyword.$0' } ],
        [ /\bbuild text search field\b/, { token: 'keyword.$0' } ],
        [ /\bclassic set name is\b/, { token: 'keyword.$0' } ],
        [ /\bcreate stamp\b/, { token: 'keyword.$0' } ],
        [ /\bupdate stamp\b/, { token: 'keyword.$0' } ],
        [ /\bshow in descending order\b/, { token: 'keyword.$0' } ],
        [ /\bNavigations\b/, { token: 'keyword.$0' } ],
        [ /\blink is\b/, { token: 'keyword.$0' } ],
        [ /\bencrypt\b/, { token: 'keyword.$0' } ],
        [ /\bis a LawsonClassicTransaction\b/, { token: 'keyword.$0' } ],
        [ /\btoken is\b/, { token: 'keyword.$0' } ],
        [ /\bTransaction Fields\b/, { token: 'keyword.$0' } ],
        [ /\bkey\b/, { token: 'keyword.$0' } ],
        [ /\bkey required\b/, { token: 'keyword.$0' } ],
        [ /\bfunction code is\b/, { token: 'keyword.$0' } ],
        [ /\breturns transaction\b/, { token: 'keyword.$0' } ],
        [ /\breturns message\b/, { token: 'keyword.$0' } ],
        [ /\bresult\b/, { token: 'keyword.$0' } ],
        [ /\bis a Find Action\b/, { token: 'keyword.$0' } ],
        [ /\buse text area\b/, { token: 'keyword.$0' } ],
        [ /\blines\b/, { token: 'keyword.$0' } ],
        [ /\bsummary form is\b/, { token: 'keyword.$0' } ],
        [ /\blevels\b/, { token: 'keyword.$0' } ],
        [ /\bentries\b/, { token: 'keyword.$0' } ],
        [ /\baction is\b/, { token: 'keyword.$0' } ],
        [ /\bis a Page\b/, { token: 'keyword.$0' } ],
        [ /\bis a MultiPanePanel\b/, { token: 'keyword.$0' } ],
        [ /\bpane\b/, { token: 'keyword.$0' } ],
        [ /\bancestors\b/, { token: 'keyword.$0' } ],
        [ /\bdisplay only\b/, { token: 'keyword.$0' } ],
        [ /\bstore as BusinessObjectReference\b/, { token: 'keyword.$0' } ],
        [ /\bcontext form is\b/, { token: 'keyword.$0' } ],
        [ /\ball business classes\b/, { token: 'keyword.$0' } ],
        [ /\ball fields\b/, { token: 'keyword.$0' } ],
        [ /\ball business tasks\b/, { token: 'keyword.$0' } ],
        [ /\ball forms\b/, { token: 'keyword.$0' } ],
        [ /\bType\b/, { token: 'keyword.$0' } ],
        [ /\bclassic name for\b/, { token: 'keyword.$0' } ],
        [ /\bsend email\b/, { token: 'keyword.$0' } ],
        [ /\bcc\b/, { token: 'keyword.$0' } ],
        [ /\bbcc\b/, { token: 'keyword.$0' } ],
        [ /\bsubject\b/, { token: 'keyword.$0' } ],
        [ /\battachment\b/, { token: 'keyword.$0' } ],
        [ /\bContents\b/, { token: 'keyword.$0' } ],
        [ /\bstored in\b/, { token: 'keyword.$0' } ],
        [ /\bis a Menu\b/, { token: 'keyword.$0' } ],
        [ /\bMenu Items\b/, { token: 'keyword.$0' } ],
        [ /\bpage is\b/, { token: 'keyword.$0' } ],
        [ /\bmenu is\b/, { token: 'keyword.$0' } ],
        [ /\benvironment\b/, { token: 'keyword.$0' } ],
        [ /\bcontext\b/, { token: 'keyword.$0' } ],
        [ /\bcreate all instances\b/, { token: 'keyword.$0' } ],
        [ /\bcreate instance\b/, { token: 'keyword.$0' } ],
        [ /\bstore using\b/, { token: 'keyword.$0' } ],
        [ /\bregister new actor\b/, { token: 'keyword.$0' } ],
        [ /\blogin name is\b/, { token: 'keyword.$0' } ],
        [ /\bpassword is\b/, { token: 'keyword.$0' } ],
        [ /\brole is\b/, { token: 'keyword.$0' } ],
        [ /\bperson name is\b/, { token: 'keyword.$0' } ],
        [ /\bsort order is\b/, { token: 'keyword.$0' } ],
        [ /\bconfig\b/, { token: 'keyword.$0' } ],
        [ /\bvariable decimal\b/, { token: 'keyword.$0' } ],
        [ /\bimplements ManualTransaction\b/, { token: 'keyword.$0' } ],
        [ /\bselect is\b/, { token: 'keyword.$0' } ],
        [ /\btemplate is\b/, { token: 'keyword.$0' } ],
        [ /\bimplements MutuallyExclusive\b/, { token: 'keyword.$0' } ],
        [ /\bfield determiner is\b/, { token: 'keyword.$0' } ],
        [ /\bfield is\b/, { token: 'keyword.$0' } ],
        [ /\bis a SummaryForm\b/, { token: 'keyword.$0' } ],
        [ /\bnumber\b/, { token: 'keyword.$0' } ],
        [ /\bexcluding\b/, { token: 'keyword.$0' } ],
        [ /\bimplements Hierarchy\b/, { token: 'keyword.$0' } ],
        [ /\bnavigation is\b/, { token: 'keyword.$0' } ],
        [ /\bSpecial Actions\b/, { token: 'keyword.$0' } ],
        [ /\bexistence not required\b/, { token: 'keyword.$0' } ],
        [ /\banyorall\b/, { token: 'keyword.$0' } ],
        [ /\bagent\b/, { token: 'keyword.$0' } ],
        [ /\buse for action\b/, { token: 'keyword.$0' } ],
        [ /\bdetail list is\b/, { token: 'keyword.$0' } ],
        [ /\bimplements ParentHierarchy\b/, { token: 'keyword.$0' } ],
        [ /\bwhile\b/, { token: 'keyword.$0' } ],
        [ /\bnext\b/, { token: 'keyword.$0' } ],
        [ /\bprevious\b/, { token: 'keyword.$0' } ],
        [ /\bfirst iteration of\b/, { token: 'keyword.$0' } ],
        [ /\blast iteration of\b/, { token: 'keyword.$0' } ],
        [ /\bend while\b/, { token: 'keyword.$0' } ],
        [ /\bend for each\b/, { token: 'keyword.$0' } ],
        [ /\bForm Invokes\b/, { token: 'keyword.$0' } ],
        [ /\bimplements FrameworkTask\b/, { token: 'keyword.$0' } ],
        [ /\bis a WebApp\b/, { token: 'keyword.$0' } ],
        [ /\bhome page is\b/, { token: 'keyword.$0' } ],
        [ /\bnavigation bar is\b/, { token: 'keyword.$0' } ],
        [ /\bregistration action is\b/, { token: 'keyword.$0' } ],
        [ /\bContext Form\b/, { token: 'keyword.$0' } ],
        [ /\bcreate action is\b/, { token: 'keyword.$0' } ],
        [ /\bopen action is\b/, { token: 'keyword.$0' } ],
        [ /\brestrict action\b/, { token: 'keyword.$0' } ],
        [ /\bis a MatrixForm\b/, { token: 'keyword.$0' } ],
        [ /\bimplements CompoundDocument\b/, { token: 'keyword.$0' } ],
        [ /\bDocument Components\b/, { token: 'keyword.$0' } ],
        [ /\bsix column\b/, { token: 'keyword.$0' } ],
        [ /\bseven column\b/, { token: 'keyword.$0' } ],
        [ /\bcolumn4\b/, { token: 'keyword.$0' } ],
        [ /\bcolumn5\b/, { token: 'keyword.$0' } ],
        [ /\bcolumn6\b/, { token: 'keyword.$0' } ],
        [ /\bcolumn7\b/, { token: 'keyword.$0' } ],
        [ /\bRollback Rules\b/, { token: 'keyword.$0' } ],
        [ /\ballow update\b/, { token: 'keyword.$0' } ],
        [ /\bimplements Encrypted\b/, { token: 'keyword.$0' } ],
        [ /\bform invoke is\b/, { token: 'keyword.$0' } ],
        [ /\bautomatic login\b/, { token: 'keyword.$0' } ],
        [ /\bbold\b/, { token: 'keyword.$0' } ],
        [ /\bitalics\b/, { token: 'keyword.$0' } ],
        [ /\bheader2\b/, { token: 'keyword.$0' } ],
        [ /\bheader3\b/, { token: 'keyword.$0' } ],
        [ /\bcheck control\b/, { token: 'keyword.$0' } ],
        [ /\bchecked state is\b/, { token: 'keyword.$0' } ],
        [ /\bcheck action is\b/, { token: 'keyword.$0' } ],
        [ /\buncheck action is\b/, { token: 'keyword.$0' } ],
        [ /\brows are\b/, { token: 'keyword.$0' } ],
        [ /\bcolumn is\b/, { token: 'keyword.$0' } ],
        [ /\bColumn\b/, { token: 'keyword.$0' } ],
        [ /\bcell is\b/, { token: 'keyword.$0' } ],
        [ /\bDetail Fields\b/, { token: 'keyword.$0' } ],
        [ /\bMatrix Forms\b/, { token: 'keyword.$0' } ],
        [ /\bCell\b/, { token: 'keyword.$0' } ],
        [ /\bgrid\b/, { token: 'keyword.$0' } ],
        [ /\bcontact info is\b/, { token: 'keyword.$0' } ],
        [ /\bis default\b/, { token: 'keyword.$0' } ],
        [ /\bdate\b/, { token: 'keyword.$0' } ],
        [ /\btime\b/, { token: 'keyword.$0' } ],
        [ /\bregistration key is\b/, { token: 'keyword.$0' } ],
        [ /\bsession\b/, { token: 'keyword.$0' } ],
        [ /\breference to\b/, { token: 'keyword.$0' } ],
        [ /\btext of\b/, { token: 'keyword.$0' } ],
        [ /\bweek day\b/, { token: 'keyword.$0' } ],
        [ /\byear day\b/, { token: 'keyword.$0' } ],
        [ /\bweek\b/, { token: 'keyword.$0' } ],
        [ /\bhours\b/, { token: 'keyword.$0' } ],
        [ /\bminutes\b/, { token: 'keyword.$0' } ],
        [ /\bseconds\b/, { token: 'keyword.$0' } ],
        [ /\bfinish form is\b/, { token: 'keyword.$0' } ],
        [ /\bmouse over text is\b/, { token: 'keyword.$0' } ],
        [ /\bblank line\b/, { token: 'keyword.$0' } ],
        [ /\ball audit views\b/, { token: 'keyword.$0' } ],
        [ /\bcancel action is\b/, { token: 'keyword.$0' } ],
        [ /\bgray\b/, { token: 'keyword.$0' } ],
        [ /\bheader6 of\b/, { token: 'keyword.$0' } ],
        [ /\bheader1\b/, { token: 'keyword.$0' } ],
        [ /\bheader4\b/, { token: 'keyword.$0' } ],
        [ /\bheader5\b/, { token: 'keyword.$0' } ],
        [ /\bheader6\b/, { token: 'keyword.$0' } ],
        [ /\blevel key\b/, { token: 'keyword.$0' } ],
        [ /\bimplements TreeView\b/, { token: 'keyword.$0' } ],
        [ /\bimplements ClassicTable\b/, { token: 'keyword.$0' } ],
        [ /\ballow updates\b/, { token: 'keyword.$0' } ],
        [ /\bhas attachments\b/, { token: 'keyword.$0' } ],
        [ /\bis comment of type\b/, { token: 'keyword.$0' } ],
        [ /\bimplements Wizard\b/, { token: 'keyword.$0' } ],
        [ /\bregistration\b/, { token: 'keyword.$0' } ],
        [ /\bsignin\b/, { token: 'keyword.$0' } ],
        [ /\bsignout\b/, { token: 'keyword.$0' } ],
        [ /\bas years\b/, { token: 'keyword.$0' } ],
        [ /\bas months\b/, { token: 'keyword.$0' } ],
        [ /\bas days\b/, { token: 'keyword.$0' } ],
        [ /\bdataarea\b/, { token: 'keyword.$0' } ],
        [ /\bcompletion message is\b/, { token: 'keyword.$0' } ],
        [ /\byears\b/, { token: 'keyword.$0' } ],
        [ /\bmonths\b/, { token: 'keyword.$0' } ],
        [ /\bdays\b/, { token: 'keyword.$0' } ],
        [ /\bdays in month\b/, { token: 'keyword.$0' } ],
        [ /\bdays in year\b/, { token: 'keyword.$0' } ],
        [ /\bdisallow navigations\b/, { token: 'keyword.$0' } ],
        [ /\bbullet\b/, { token: 'keyword.$0' } ],
        [ /\bstyle is\b/, { token: 'keyword.$0' } ],
        [ /\bpending\b/, { token: 'keyword.$0' } ],
        [ /\bcolumns are\b/, { token: 'keyword.$0' } ],
        [ /\bcustom home page is\b/, { token: 'keyword.$0' } ],
        [ /\buse custom login\b/, { token: 'keyword.$0' } ],
        [ /\bparent\b/, { token: 'keyword.$0' } ],
        [ /\bsiblings\b/, { token: 'keyword.$0' } ],
        [ /\bdescendants\b/, { token: 'keyword.$0' } ],
        [ /\bascendant\b/, { token: 'keyword.$0' } ],
        [ /\bchildren\b/, { token: 'keyword.$0' } ],
        [ /\bupdate actor\b/, { token: 'keyword.$0' } ],
        [ /\bCreate Rules\b/, { token: 'keyword.$0' } ],
        [ /\bDelete Rules\b/, { token: 'keyword.$0' } ],
        [ /\bimplements FieldRange\b/, { token: 'keyword.$0' } ],
        [ /\bfrom is\b/, { token: 'keyword.$0' } ],
        [ /\bto is\b/, { token: 'keyword.$0' } ],
        [ /\bexclusive\b/, { token: 'keyword.$0' } ],
        [ /\boverlaps\b/, { token: 'keyword.$0' } ],
        [ /\bdisplay\b/, { token: 'keyword.$0' } ],
        [ /\bforce default\b/, { token: 'keyword.$0' } ],
        [ /\bdynamic default\b/, { token: 'keyword.$0' } ],
        [ /\bdisable resume on error\b/, { token: 'keyword.$0' } ],
        [ /\brpg name is\b/, { token: 'keyword.$0' } ],
        [ /\bLabelField\b/, { token: 'keyword.$0' } ],
        [ /\bauto refresh using\b/, { token: 'keyword.$0' } ],
        [ /\braise red alert\b/, { token: 'keyword.$0' } ],
        [ /\braise yellow alert\b/, { token: 'keyword.$0' } ],
        [ /\braise green alert\b/, { token: 'keyword.$0' } ],
        [ /\band siblings\b/, { token: 'keyword.$0' } ],
        [ /\band descendants\b/, { token: 'keyword.$0' } ],
        [ /\band children\b/, { token: 'keyword.$0' } ],
        [ /\band ancestors\b/, { token: 'keyword.$0' } ],
        [ /\bdisplay date\b/, { token: 'keyword.$0' } ],
        [ /\bdisplay time\b/, { token: 'keyword.$0' } ],
        [ /\bdisplay hours and minutes\b/, { token: 'keyword.$0' } ],
        [ /\bimplements ChildHierarchy\b/, { token: 'keyword.$0' } ],
        [ /\bchildren are\b/, { token: 'keyword.$0' } ],
        [ /\bdisable create within\b/, { token: 'keyword.$0' } ],
        [ /\blink is row\b/, { token: 'keyword.$0' } ],
        [ /\bincrement\b/, { token: 'keyword.$0' } ],
        [ /\bdecrement\b/, { token: 'keyword.$0' } ],
        [ /\bimplements Versioning\b/, { token: 'keyword.$0' } ],
        [ /\bversion field is\b/, { token: 'keyword.$0' } ],
        [ /\bversion is\b/, { token: 'keyword.$0' } ],
        [ /\breturn\b/, { token: 'keyword.$0' } ],
        [ /\btext search field is\b/, { token: 'keyword.$0' } ],
        [ /\badvanced search is\b/, { token: 'keyword.$0' } ],
        [ /\bdisable Auditing\b/, { token: 'keyword.$0' } ],
        [ /\bdisable EffectiveDated\b/, { token: 'keyword.$0' } ],
        [ /\bdisable UniqueID\b/, { token: 'keyword.$0' } ],
        [ /\bparent field name is\b/, { token: 'keyword.$0' } ],
        [ /\bImplements Framework Pattern\b/, { token: 'keyword.$0' } ],
        [ /\bexact version is\b/, { token: 'keyword.$0' } ],
        [ /\bprecision is\b/, { token: 'keyword.$0' } ],
        [ /\bis a MovexInterface\b/, { token: 'keyword.$0' } ],
        [ /\bprogram is\b/, { token: 'keyword.$0' } ],
        [ /\bInput Record\b/, { token: 'keyword.$0' } ],
        [ /\bOutput Record\b/, { token: 'keyword.$0' } ],
        [ /\bis a List Action\b/, { token: 'keyword.$0' } ],
        [ /\blinkback\b/, { token: 'keyword.$0' } ],
        [ /\bwebapp is\b/, { token: 'keyword.$0' } ],
        [ /\btext is\b/, { token: 'keyword.$0' } ],
        [ /\bis a RequestAction\b/, { token: 'keyword.$0' } ],
        [ /\bis a Create RequestAction\b/, { token: 'keyword.$0' } ],
        [ /\bis an Update RequestAction\b/, { token: 'keyword.$0' } ],
        [ /\bis a Delete RequestAction\b/, { token: 'keyword.$0' } ],
        [ /\bis an Instance RequestAction\b/, { token: 'keyword.$0' } ],
        [ /\bis a Set RequestAction\b/, { token: 'keyword.$0' } ],
        [ /\bcannot be empty\b/, { token: 'keyword.$0' } ],
        [ /\bconfirmation required on empty\b/, { token: 'keyword.$0' } ],
        [ /\brequest action process is\b/, { token: 'keyword.$0' } ],
        [ /\bimplements ForeignTable\b/, { token: 'keyword.$0' } ],
        [ /\bcancel\b/, { token: 'keyword.$0' } ],
        [ /\bis a WorkViewPanel\b/, { token: 'keyword.$0' } ],
        [ /\bshow\b/, { token: 'keyword.$0' } ],
        [ /\bdisable RetroactiveEffectiveDating\b/, { token: 'keyword.$0' } ],
        [ /\bAdditional Folder Items\b/, { token: 'keyword.$0' } ],
        [ /\breason code required\b/, { token: 'keyword.$0' } ],
        [ /\bis a WebserviceInterface\b/, { token: 'keyword.$0' } ],
        [ /\bwsdlURL is\b/, { token: 'keyword.$0' } ],
        [ /\bendpoint is\b/, { token: 'keyword.$0' } ],
        [ /\bservice is\b/, { token: 'keyword.$0' } ],
        [ /\bnamespace is\b/, { token: 'keyword.$0' } ],
        [ /\belement name is\b/, { token: 'keyword.$0' } ],
        [ /\battribute name is\b/, { token: 'keyword.$0' } ],
        [ /\bservice style is\b/, { token: 'keyword.$0' } ],
        [ /\bmethod is\b/, { token: 'keyword.$0' } ],
        [ /\bURLs\b/, { token: 'keyword.$0' } ],
        [ /\burl is\b/, { token: 'keyword.$0' } ],
        [ /\bsearch form is\b/, { token: 'keyword.$0' } ],
        [ /\bmulti-select field is\b/, { token: 'keyword.$0' } ],
        [ /\bdisplay field is\b/, { token: 'keyword.$0' } ],
        [ /\buse\b/, { token: 'keyword.$0' } ],
        [ /\bgroup\b/, { token: 'keyword.$0' } ],
        [ /\bin subject\b/, { token: 'keyword.$0' } ],
        [ /\bsql name for\b/, { token: 'keyword.$0' } ],
        [ /\bname for\b/, { token: 'keyword.$0' } ],
        [ /\btype for\b/, { token: 'keyword.$0' } ],
        [ /\bapprover\b/, { token: 'keyword.$0' } ],
        [ /\bfinal approver\b/, { token: 'keyword.$0' } ],
        [ /\binitiator\b/, { token: 'keyword.$0' } ],
        [ /\binitial value is\b/, { token: 'keyword.$0' } ],
        [ /\bchanged between\b/, { token: 'keyword.$0' } ],
        [ /\bnever asynchronous\b/, { token: 'keyword.$0' } ],
        [ /\balways asynchronous\b/, { token: 'keyword.$0' } ],
        [ /\bText Variables\b/, { token: 'keyword.$0' } ],
        [ /\bfirst date within\b/, { token: 'keyword.$0' } ],
        [ /\blast date within\b/, { token: 'keyword.$0' } ],
        [ /\btext\b/, { token: 'keyword.$0' } ],
        [ /\brun synchronous\b/, { token: 'keyword.$0' } ],
        [ /\brun asynchronous\b/, { token: 'keyword.$0' } ],
        [ /\bimplements ArrayHierarchy\b/, { token: 'keyword.$0' } ],
        [ /\bdefault label is\b/, { token: 'keyword.$0' } ],
        [ /\bsubject is\b/, { token: 'keyword.$0' } ],
        [ /\baction comment required\b/, { token: 'keyword.$0' } ],
        [ /\bDetail Sections\b/, { token: 'keyword.$0' } ],
        [ /\bin foreground\b/, { token: 'keyword.$0' } ],
        [ /\bin background\b/, { token: 'keyword.$0' } ],
        [ /\brun in foreground\b/, { token: 'keyword.$0' } ],
        [ /\brun in background\b/, { token: 'keyword.$0' } ],
        [ /\bposition to\b/, { token: 'keyword.$0' } ],
        [ /\bimplements MI DataSource\b/, { token: 'keyword.$0' } ],
        [ /\bimplements LCT DataSource\b/, { token: 'keyword.$0' } ],
        [ /\bimplements WSI DataSource\b/, { token: 'keyword.$0' } ],
        [ /\bDataSource Mapping\b/, { token: 'keyword.$0' } ],
        [ /\bset is\b/, { token: 'keyword.$0' } ],
        [ /\bfind is\b/, { token: 'keyword.$0' } ],
        [ /\bcreate is\b/, { token: 'keyword.$0' } ],
        [ /\bupdate is\b/, { token: 'keyword.$0' } ],
        [ /\bdelete is\b/, { token: 'keyword.$0' } ],
        [ /\bdatasource\b/, { token: 'keyword.$0' } ],
        [ /\bis a Purge Action\b/, { token: 'keyword.$0' } ],
        [ /\bis a Purge RequestAction\b/, { token: 'keyword.$0' } ],
        [ /\banniversary\b/, { token: 'keyword.$0' } ],
        [ /\bBelow\b/, { token: 'keyword.$0' } ],
        [ /\bOn Side\b/, { token: 'keyword.$0' } ],
        [ /\blast changed date\b/, { token: 'keyword.$0' } ],
        [ /\bAll Fields for\b/, { token: 'keyword.$0' } ],
        [ /\bare accessible\b/, { token: 'keyword.$0' } ],
        [ /\bare not accessible\b/, { token: 'keyword.$0' } ],
        [ /\bimplements Resequence on\b/, { token: 'keyword.$0' } ],
        [ /\bnew sequence field is\b/, { token: 'keyword.$0' } ],
        [ /\bimplements M3ClassicTable\b/, { token: 'keyword.$0' } ],
        [ /\bimplements S3ClassicTable\b/, { token: 'keyword.$0' } ],
        [ /\bcreate date\b/, { token: 'keyword.$0' } ],
        [ /\bdate first changed\b/, { token: 'keyword.$0' } ],
        [ /\bdate last changed\b/, { token: 'keyword.$0' } ],
        [ /\bdate next changed\b/, { token: 'keyword.$0' } ],
        [ /\bdays was\b/, { token: 'keyword.$0' } ],
        [ /\bbetween\b/, { token: 'keyword.$0' } ],
        [ /\bweighted average\b/, { token: 'keyword.$0' } ],
        [ /\bdocument template for\b/, { token: 'keyword.$0' } ],
        [ /\bdocument\b/, { token: 'keyword.$0' } ],
        [ /\btranslatable\b/, { token: 'keyword.$0' } ],
        [ /\bdisable auditing\b/, { token: 'keyword.$0' } ],
        [ /\bWebApp\b/, { token: 'keyword.$0' } ],
        [ /\bDrill List\b/, { token: 'keyword.$0' } ],
        [ /\bpropagate as a drill\b/, { token: 'keyword.$0' } ],
        [ /\bdrill restricted\b/, { token: 'keyword.$0' } ],
        [ /\bimplements SurrogateAuditLog for\b/, { token: 'keyword.$0' } ],
        [ /\baudit log records\b/, { token: 'keyword.$0' } ],
        [ /\baction\b/, { token: 'keyword.$0' } ],
        [ /\bexact\b/, { token: 'keyword.$0' } ],
        [ /\bsession key\b/, { token: 'keyword.$0' } ],
        [ /\bmime type is\b/, { token: 'keyword.$0' } ],
        [ /\brepeating\b/, { token: 'keyword.$0' } ],
        [ /\bshow as pdf\b/, { token: 'keyword.$0' } ],
        [ /\bcolumn visible when\b/, { token: 'keyword.$0' } ],
        [ /\bdisallow show as pdf\b/, { token: 'keyword.$0' } ],
        [ /\bCharts\b/, { token: 'keyword.$0' } ],
        [ /\bis a Bar Chart\b/, { token: 'keyword.$0' } ],
        [ /\bis a Pie Chart\b/, { token: 'keyword.$0' } ],
        [ /\belement value is\b/, { token: 'keyword.$0' } ],
        [ /\bassign message group id to\b/, { token: 'keyword.$0' } ],
        [ /\bis a BarChart\b/, { token: 'keyword.$0' } ],
        [ /\bis a PieChart\b/, { token: 'keyword.$0' } ],
        [ /\bis a GapChart\b/, { token: 'keyword.$0' } ],
        [ /\bdisplay as link\b/, { token: 'keyword.$0' } ],
        [ /\bdisplay as\b/, { token: 'keyword.$0' } ],
        [ /\bimplements OrgChartView\b/, { token: 'keyword.$0' } ],
        [ /\bline1 text is\b/, { token: 'keyword.$0' } ],
        [ /\bline2 text is\b/, { token: 'keyword.$0' } ],
        [ /\braise red alert when\b/, { token: 'keyword.$0' } ],
        [ /\braise yellow alert when\b/, { token: 'keyword.$0' } ],
        [ /\braise green alert when\b/, { token: 'keyword.$0' } ],
        [ /\blegend state is\b/, { token: 'keyword.$0' } ],
        [ /\bdisplay zero\b/, { token: 'keyword.$0' } ],
        [ /\bis a M3Interface\b/, { token: 'keyword.$0' } ],
        [ /\bdisplay as meter chart\b/, { token: 'keyword.$0' } ],
        [ /\bdisplay as thermometer chart\b/, { token: 'keyword.$0' } ],
        [ /\bmeter range is\b/, { token: 'keyword.$0' } ],
        [ /\bis a SearchForm\b/, { token: 'keyword.$0' } ],
        [ /\bsearch field is\b/, { token: 'keyword.$0' } ],
        [ /\bdisplay as duration\b/, { token: 'keyword.$0' } ],
        [ /\bauthentication is\b/, { token: 'keyword.$0' } ],
        [ /\bis a LineChart\b/, { token: 'keyword.$0' } ],
        [ /\bred alert range is from\b/, { token: 'keyword.$0' } ],
        [ /\bgreen alert range is from\b/, { token: 'keyword.$0' } ],
        [ /\byellow alert range is from\b/, { token: 'keyword.$0' } ],
        [ /\buse as filter\b/, { token: 'keyword.$0' } ],
        [ /\balways display\b/, { token: 'keyword.$0' } ],
        [ /\btotal\b/, { token: 'keyword.$0' } ],
        [ /\brunning total\b/, { token: 'keyword.$0' } ],
        [ /\bpercent of total\b/, { token: 'keyword.$0' } ],
        [ /\bshow totals only\b/, { token: 'keyword.$0' } ],
        [ /\bsuppress sub-totals\b/, { token: 'keyword.$0' } ],
        [ /\bimplements EnterpriseSearch using\b/, { token: 'keyword.$0' } ],
        [ /\bAliases\b/, { token: 'keyword.$0' } ],
        [ /\bshow in relevance order\b/, { token: 'keyword.$0' } ],
        [ /\bapplication messages\b/, { token: 'keyword.$0' } ],
        [ /\bdatasource paging\b/, { token: 'keyword.$0' } ],
        [ /\beffective date required\b/, { token: 'keyword.$0' } ],
        [ /\bentry stamp\b/, { token: 'keyword.$0' } ],
        [ /\bleft of field\b/, { token: 'keyword.$0' } ],
        [ /\bright of field\b/, { token: 'keyword.$0' } ],
        [ /\bAttachments\b/, { token: 'keyword.$0' } ],
        [ /\bwas\b/, { token: 'keyword.$0' } ],
        [ /\balways\b/, { token: 'keyword.$0' } ],
        [ /\bversion\b/, { token: 'keyword.$0' } ],
        [ /\bapplied stamp\b/, { token: 'keyword.$0' } ],
        [ /\baction type\b/, { token: 'keyword.$0' } ],
        [ /\bpast\b/, { token: 'keyword.$0' } ],
        [ /\bcurrent\b/, { token: 'keyword.$0' } ],
        [ /\bfuture\b/, { token: 'keyword.$0' } ],
        [ /\bMenu\b/, { token: 'keyword.$0' } ],
        [ /\bMenuItem\b/, { token: 'keyword.$0' } ],
        [ /\bresume on error\b/, { token: 'keyword.$0' } ],
        [ /\bdisallow data export\b/, { token: 'keyword.$0' } ],
        [ /\bMenus\b/, { token: 'keyword.$0' } ],
        [ /\bis an AuditList\b/, { token: 'keyword.$0' } ],
        [ /\baudit period\b/, { token: 'keyword.$0' } ],
        [ /\bAll Menus for\b/, { token: 'keyword.$0' } ],
        [ /\bAll MenuItems for\b/, { token: 'keyword.$0' } ],
        [ /\baudit entry id\b/, { token: 'keyword.$0' } ],
        [ /\bby\b/, { token: 'keyword.$0' } ],
        [ /\bsearch\b/, { token: 'keyword.$0' } ],
        [ /\bmanual transaction\b/, { token: 'keyword.$0' } ],
        [ /\bparameter\b/, { token: 'keyword.$0' } ],
        [ /\bsub panels are\b/, { token: 'keyword.$0' } ],
        [ /\bQuick Entry Fields\b/, { token: 'keyword.$0' } ],
        [ /\bshow fixed scale\b/, { token: 'keyword.$0' } ],
        [ /\bdisplay as radio buttons\b/, { token: 'keyword.$0' } ],
        [ /\bis a ScatterChart\b/, { token: 'keyword.$0' } ],
        [ /\bpoint size is\b/, { token: 'keyword.$0' } ],
        [ /\bx-axis value is\b/, { token: 'keyword.$0' } ],
        [ /\by-axis value is\b/, { token: 'keyword.$0' } ],
        [ /\bimplements FutureCorrection\b/, { token: 'keyword.$0' } ],
        [ /\bimplements HistoryCorrection\b/, { token: 'keyword.$0' } ],
        [ /\bEmpty Set Rules\b/, { token: 'keyword.$0' } ],
        [ /\bshow editor for\b/, { token: 'keyword.$0' } ],
        [ /\bexport\b/, { token: 'keyword.$0' } ],
        [ /\brange\b/, { token: 'keyword.$0' } ],
        [ /\bhigh value\b/, { token: 'keyword.$0' } ],
        [ /\bSummary Total Fields\b/, { token: 'keyword.$0' } ],
        [ /\binstance count\b/, { token: 'keyword.$0' } ],
        [ /\ball navigations\b/, { token: 'keyword.$0' } ],
        [ /\bdata export\b/, { token: 'keyword.$0' } ],
        [ /\bshow grid lines\b/, { token: 'keyword.$0' } ],
        [ /\beffective until\b/, { token: 'keyword.$0' } ],
        [ /\bcorrection\b/, { token: 'keyword.$0' } ],
        [ /\bshow panel navigation\b/, { token: 'keyword.$0' } ],
        [ /\bsystem stamp\b/, { token: 'keyword.$0' } ],
        [ /\btimestamp\b/, { token: 'keyword.$0' } ],
        [ /\brequest action linkback webapp is\b/, { token: 'keyword.$0' } ],
        [ /\bdisable checkpoint\b/, { token: 'keyword.$0' } ],
        [ /\benvelope is\b/, { token: 'keyword.$0' } ],
        [ /\bexternal\b/, { token: 'keyword.$0' } ],
        [ /\bdisplay as horizontal radio buttons\b/, { token: 'keyword.$0' } ],
        [ /\brelevance score\b/, { token: 'keyword.$0' } ],
        [ /\bcancel resume\b/, { token: 'keyword.$0' } ],
        [ /\bDimensions\b/, { token: 'keyword.$0' } ],
        [ /\bMeasures\b/, { token: 'keyword.$0' } ],
        [ /\bimplements CubeView\b/, { token: 'keyword.$0' } ],
        [ /\bRow Dimensions\b/, { token: 'keyword.$0' } ],
        [ /\bvalid for dimension\b/, { token: 'keyword.$0' } ],
        [ /\bvisible for dimension\b/, { token: 'keyword.$0' } ],
        [ /\bsystem current date\b/, { token: 'keyword.$0' } ],
        [ /\bsystem current year\b/, { token: 'keyword.$0' } ],
        [ /\bsystem current period\b/, { token: 'keyword.$0' } ],
        [ /\bsystem current timestamp\b/, { token: 'keyword.$0' } ],
        [ /\bsystem current anniversary\b/, { token: 'keyword.$0' } ],
        [ /\bperiod dimension\b/, { token: 'keyword.$0' } ],
        [ /\bmonthly\b/, { token: 'keyword.$0' } ],
        [ /\bweekly\b/, { token: 'keyword.$0' } ],
        [ /\benable EffectiveDated\b/, { token: 'keyword.$0' } ],
        [ /\berror message\b/, { token: 'keyword.$0' } ],
        [ /\bdisable select in webui\b/, { token: 'keyword.$0' } ],
        [ /\bshow steps in webui\b/, { token: 'keyword.$0' } ],
        [ /\bFirst Page Header\b/, { token: 'keyword.$0' } ],
        [ /\bPage Header\b/, { token: 'keyword.$0' } ],
        [ /\bPage Footer\b/, { token: 'keyword.$0' } ],
        [ /\bpage number\b/, { token: 'keyword.$0' } ],
        [ /\bdisplay when blank\b/, { token: 'keyword.$0' } ],
        [ /\bshow border\b/, { token: 'keyword.$0' } ],
        [ /\bshade\b/, { token: 'keyword.$0' } ],
        [ /\bunderline\b/, { token: 'keyword.$0' } ],
        [ /\bvariable name is\b/, { token: 'keyword.$0' } ],
        [ /\bauthenticated actor\b/, { token: 'keyword.$0' } ],
        [ /\bvertical split is\b/, { token: 'keyword.$0' } ],
        [ /\blabel\b/, { token: 'keyword.$0' } ],
        [ /\blocale of\b/, { token: 'keyword.$0' } ],
        [ /\bis a QuadrantChart\b/, { token: 'keyword.$0' } ],
        [ /\brow title is\b/, { token: 'keyword.$0' } ],
        [ /\bcolumn title is\b/, { token: 'keyword.$0' } ],
        [ /\bshow unplaced elements\b/, { token: 'keyword.$0' } ],
        [ /\bimplements DragAndDrop\b/, { token: 'keyword.$0' } ],
        [ /\bquadrants are\b/, { token: 'keyword.$0' } ],
        [ /\bquadrant value is\b/, { token: 'keyword.$0' } ],
        [ /\brow position is\b/, { token: 'keyword.$0' } ],
        [ /\bcolumn position is\b/, { token: 'keyword.$0' } ],
        [ /\bheader is\b/, { token: 'keyword.$0' } ],
        [ /\bdisplay as text\b/, { token: 'keyword.$0' } ],
        [ /\bdrop target\b/, { token: 'keyword.$0' } ],
        [ /\bis a CardView\b/, { token: 'keyword.$0' } ],
        [ /\bleft column is\b/, { token: 'keyword.$0' } ],
        [ /\bright column is\b/, { token: 'keyword.$0' } ],
        [ /\bmissing image is\b/, { token: 'keyword.$0' } ],
        [ /\bnode icon is\b/, { token: 'keyword.$0' } ],
        [ /\bmain column\b/, { token: 'keyword.$0' } ],
        [ /\bcard view is\b/, { token: 'keyword.$0' } ],
        [ /\bimplements FixedSizeList\b/, { token: 'keyword.$0' } ],
        [ /\bleft column\b/, { token: 'keyword.$0' } ],
        [ /\bright column\b/, { token: 'keyword.$0' } ],
        [ /\bis a ColumnarPanel\b/, { token: 'keyword.$0' } ],
        [ /\bshow steps\b/, { token: 'keyword.$0' } ],
        [ /\bshow panel control on left\b/, { token: 'keyword.$0' } ],
        [ /\bmax levels is\b/, { token: 'keyword.$0' } ],
        [ /\bwith\b/, { token: 'keyword.$0' } ],
        [ /\bweek year\b/, { token: 'keyword.$0' } ],
        [ /\bphoto image\b/, { token: 'keyword.$0' } ],
        [ /\bicon is\b/, { token: 'keyword.$0' } ],
        [ /\bimplements HorizontalScrolling\b/, { token: 'keyword.$0' } ],
        [ /\bfreeze first\b/, { token: 'keyword.$0' } ],
        [ /\bcolumn\b/, { token: 'keyword.$0' } ],
        [ /\bcolumns\b/, { token: 'keyword.$0' } ],
        [ /\bpanel text is\b/, { token: 'keyword.$0' } ],
        [ /\brow separator is\b/, { token: 'keyword.$0' } ],
        [ /\bhorizontal rule\b/, { token: 'keyword.$0' } ],
        [ /\bmatches\b/, { token: 'keyword.$0' } ],
        [ /\bhas audit change\b/, { token: 'keyword.$0' } ],
        [ /\bcolor of\b/, { token: 'keyword.$0' } ],
        [ /\bred\b/, { token: 'keyword.$0' } ],
        [ /\bgreen\b/, { token: 'keyword.$0' } ],
        [ /\bblue\b/, { token: 'keyword.$0' } ],
        [ /\byellow\b/, { token: 'keyword.$0' } ],
        [ /\bjade\b/, { token: 'keyword.$0' } ],
        [ /\bmagenta\b/, { token: 'keyword.$0' } ],
        [ /\bmaroon\b/, { token: 'keyword.$0' } ],
        [ /\borange\b/, { token: 'keyword.$0' } ],
        [ /\bbrown\b/, { token: 'keyword.$0' } ],
        [ /\bhidden\b/, { token: 'keyword.$0' } ],
        [ /\ball global UI configuration\b/, { token: 'keyword.$0' } ],
        [ /\bdescription is\b/, { token: 'keyword.$0' } ],
        [ /\bbutton row\b/, { token: 'keyword.$0' } ],
        [ /\bis an ImageMap\b/, { token: 'keyword.$0' } ],
        [ /\bmain image is\b/, { token: 'keyword.$0' } ],
        [ /\bdescription area is\b/, { token: 'keyword.$0' } ],
        [ /\bOverlays\b/, { token: 'keyword.$0' } ],
        [ /\bhot spot at\b/, { token: 'keyword.$0' } ],
        [ /\bid is\b/, { token: 'keyword.$0' } ],
        [ /\bvalid when id\b/, { token: 'keyword.$0' } ],
        [ /\bactive\b/, { token: 'keyword.$0' } ],
        [ /\bactive on hover\b/, { token: 'keyword.$0' } ],
        [ /\buntil new hotspot\b/, { token: 'keyword.$0' } ],
        [ /\bwith no deactivate\b/, { token: 'keyword.$0' } ],
        [ /\bHover Overlays\b/, { token: 'keyword.$0' } ],
        [ /\bPress Overlays\b/, { token: 'keyword.$0' } ],
        [ /\bdefault text properties\b/, { token: 'keyword.$0' } ],
        [ /\bfont size is\b/, { token: 'keyword.$0' } ],
        [ /\bfont color is\b/, { token: 'keyword.$0' } ],
        [ /\bvertically align\b/, { token: 'keyword.$0' } ],
        [ /\bhorizontally align\b/, { token: 'keyword.$0' } ],
        [ /\bleft\b/, { token: 'keyword.$0' } ],
        [ /\bright\b/, { token: 'keyword.$0' } ],
        [ /\btop\b/, { token: 'keyword.$0' } ],
        [ /\bbottom\b/, { token: 'keyword.$0' } ],
        [ /\bin rectangle\b/, { token: 'keyword.$0' } ],
        [ /\bblack\b/, { token: 'keyword.$0' } ],
        [ /\bat\b/, { token: 'keyword.$0' } ],
        [ /\bactive area is\b/, { token: 'keyword.$0' } ],
        [ /\bimplements HotSpot\b/, { token: 'keyword.$0' } ],
        [ /\bhover image is\b/, { token: 'keyword.$0' } ],
        [ /\bpress image is\b/, { token: 'keyword.$0' } ],
        [ /\bdisplay as progress bar\b/, { token: 'keyword.$0' } ],
        [ /\bimage map is\b/, { token: 'keyword.$0' } ],
        [ /\bdisplay as slider\b/, { token: 'keyword.$0' } ],
        [ /\brange is from\b/, { token: 'keyword.$0' } ],
        [ /\bdepends on\b/, { token: 'keyword.$0' } ],
        [ /\bderived value is\b/, { token: 'keyword.$0' } ],
        [ /\bdata area is\b/, { token: 'keyword.$0' } ],
        [ /\bimplements ExtendsExternalData\b/, { token: 'keyword.$0' } ],
        [ /\bimplements DashBoardView\b/, { token: 'keyword.$0' } ],
        [ /\boverview navigation is\b/, { token: 'keyword.$0' } ],
        [ /\bdata translation enabled\b/, { token: 'keyword.$0' } ],
        [ /\balign as label\b/, { token: 'keyword.$0' } ],
        [ /\bshow as springboard\b/, { token: 'keyword.$0' } ],
        [ /\bshow up to\b/, { token: 'keyword.$0' } ],
        [ /\bcharacters\b/, { token: 'keyword.$0' } ],
        [ /\bimplements AnalyticCube\b/, { token: 'keyword.$0' } ],
        [ /\bdisable continuous update\b/, { token: 'keyword.$0' } ],
        [ /\baction tag is\b/, { token: 'keyword.$0' } ],
        [ /\badd action tag\b/, { token: 'keyword.$0' } ],
        [ /\baction tag\b/, { token: 'keyword.$0' } ],
        [ /\bprior\b/, { token: 'keyword.$0' } ],
        [ /\bperiod\b/, { token: 'keyword.$0' } ],
        [ /\bperiods\b/, { token: 'keyword.$0' } ],
        [ /\bquarter\b/, { token: 'keyword.$0' } ],
        [ /\bquarters\b/, { token: 'keyword.$0' } ],
        [ /\bbeginning\b/, { token: 'keyword.$0' } ],
        [ /\bending\b/, { token: 'keyword.$0' } ],
        [ /\bytd\b/, { token: 'keyword.$0' } ],
        [ /\bbalance\b/, { token: 'keyword.$0' } ],
        [ /\bthru\b/, { token: 'keyword.$0' } ],
        [ /\bsuppress blank rows\b/, { token: 'keyword.$0' } ],
        [ /\bon completion navigate to\b/, { token: 'keyword.$0' } ],
        [ /\ballow non-modal interaction\b/, { token: 'keyword.$0' } ],
        [ /\bList\b/, { token: 'keyword.$0' } ],
        [ /\bperiod stamp\b/, { token: 'keyword.$0' } ],
        [ /\bQ1\b/, { token: 'keyword.$0' } ],
        [ /\bQ2\b/, { token: 'keyword.$0' } ],
        [ /\bQ3\b/, { token: 'keyword.$0' } ],
        [ /\bQ4\b/, { token: 'keyword.$0' } ],
        [ /\blocale\b/, { token: 'keyword.$0' } ],
        [ /\bgroup label is\b/, { token: 'keyword.$0' } ],
        [ /\bis a MultiListPanel\b/, { token: 'keyword.$0' } ],
        [ /\bdisable AsOfDateProcessing\b/, { token: 'keyword.$0' } ],
        [ /\bopen\b/, { token: 'keyword.$0' } ],
        [ /\bsave\b/, { token: 'keyword.$0' } ],
        [ /\bsave and new\b/, { token: 'keyword.$0' } ],
        [ /\bsave and close\b/, { token: 'keyword.$0' } ],
        [ /\bexport to pdf\b/, { token: 'keyword.$0' } ],
        [ /\bexport to csv\b/, { token: 'keyword.$0' } ],
        [ /\brefresh\b/, { token: 'keyword.$0' } ],
        [ /\baction icon is\b/, { token: 'keyword.$0' } ],
        [ /\brelease\b/, { token: 'keyword.$0' } ],
        [ /\btransfer\b/, { token: 'keyword.$0' } ],
        [ /\bsubmit\b/, { token: 'keyword.$0' } ],
        [ /\bapply\b/, { token: 'keyword.$0' } ],
        [ /\bapprove\b/, { token: 'keyword.$0' } ],
        [ /\baward\b/, { token: 'keyword.$0' } ],
        [ /\bcalculate\b/, { token: 'keyword.$0' } ],
        [ /\bchart\b/, { token: 'keyword.$0' } ],
        [ /\bclaim\b/, { token: 'keyword.$0' } ],
        [ /\bcreate\b/, { token: 'keyword.$0' } ],
        [ /\bcopy\b/, { token: 'keyword.$0' } ],
        [ /\bdelete\b/, { token: 'keyword.$0' } ],
        [ /\bedit\b/, { token: 'keyword.$0' } ],
        [ /\bfinalize\b/, { token: 'keyword.$0' } ],
        [ /\bpreview\b/, { token: 'keyword.$0' } ],
        [ /\breject\b/, { token: 'keyword.$0' } ],
        [ /\brequest\b/, { token: 'keyword.$0' } ],
        [ /\bvalidate\b/, { token: 'keyword.$0' } ],
        [ /\bundo\b/, { token: 'keyword.$0' } ],
        [ /\bhide when invalid\b/, { token: 'keyword.$0' } ],
        [ /\bhelper list\b/, { token: 'keyword.$0' } ],
        [ /\bcharts\b/, { token: 'keyword.$0' } ],
        [ /\bcontains environment data\b/, { token: 'keyword.$0' } ],
        [ /\bprimitive size is\b/, { token: 'keyword.$0' } ],
        [ /\bprimitive type is\b/, { token: 'keyword.$0' } ],
        [ /\bprimitive decimal size is\b/, { token: 'keyword.$0' } ],
        [ /\bconfigurable home page is\b/, { token: 'keyword.$0' } ],
        [ /\bconfigurable css is\b/, { token: 'keyword.$0' } ],
        [ /\bconfigurable login is\b/, { token: 'keyword.$0' } ],
        [ /\bcurrent year is\b/, { token: 'keyword.$0' } ],
        [ /\bcurrent period is\b/, { token: 'keyword.$0' } ],
        [ /\bPeriod Views\b/, { token: 'keyword.$0' } ],
        [ /\bPeriods\b/, { token: 'keyword.$0' } ],
        [ /\binvalid\b/, { token: 'keyword.$0' } ],
        [ /\bParent Attach Rules\b/, { token: 'keyword.$0' } ],
        [ /\bchild\b/, { token: 'keyword.$0' } ],
        [ /\bparent state is\b/, { token: 'keyword.$0' } ],
        [ /\baudit update\b/, { token: 'keyword.$0' } ],
        [ /\baudit create\b/, { token: 'keyword.$0' } ],
        [ /\bchange effective date\b/, { token: 'keyword.$0' } ],
        [ /\bAudit Entry Rules\b/, { token: 'keyword.$0' } ],
        [ /\bCreate Exit Rules\b/, { token: 'keyword.$0' } ],
        [ /\bimage is\b/, { token: 'keyword.$0' } ],
        [ /\brepresentative text\b/, { token: 'keyword.$0' } ],
        [ /\brepresentative image\b/, { token: 'keyword.$0' } ],
        [ /\braise blue alert\b/, { token: 'keyword.$0' } ],
        [ /\braise blue alert when\b/, { token: 'keyword.$0' } ],
        [ /\bdistributed\b/, { token: 'keyword.$0' } ],
        [ /\bminimize contention\b/, { token: 'keyword.$0' } ],
        [ /\bis framework spec\b/, { token: 'keyword.$0' } ],
        [ /\buntranslatable:\"\b/, { token: 'keyword.$0' } ],
        [ /\btransfer\b/, { token: 'keyword.$0' } ],
        [ /\bfill in user fields from\b/, { token: 'keyword.$0' } ],
        [ /\bfill in blank user fields from\b/, { token: 'keyword.$0' } ],
        [ /\ballow user fields\b/, { token: 'keyword.$0' } ],
        [ /\bas parameters\b/, { token: 'keyword.$0' } ],
        [ /\baudit delete\b/, { token: 'keyword.$0' } ],
        [ /\bcentered\b/, { token: 'keyword.$0' } ],
        [ /\bhas children when\b/, { token: 'keyword.$0' } ],
        [ /\bcolumn header is\b/, { token: 'keyword.$0' } ],
        [ /\bportrait image\b/, { token: 'keyword.$0' } ],
        [ /\bfull image\b/, { token: 'keyword.$0' } ],
        [ /\bvalidity constraint\b/, { token: 'keyword.$0' } ],
        [ /\bis an ActionOverride\b/, { token: 'keyword.$0' } ],
        [ /\ball security configuration\b/, { token: 'keyword.$0' } ],
        [ /\ball personalization\b/, { token: 'keyword.$0' } ],
        [ /\ball scheduled actions\b/, { token: 'keyword.$0' } ],
        [ /\bdata menu\b/, { token: 'keyword.$0' } ],
        [ /\bdisable saved search\b/, { token: 'keyword.$0' } ],
        [ /\bsend notification\b/, { token: 'keyword.$0' } ],
        [ /\bdetail is\b/, { token: 'keyword.$0' } ],
        [ /\bpriority is\b/, { token: 'keyword.$0' } ],
        [ /\bvery low\b/, { token: 'keyword.$0' } ],
        [ /\blow\b/, { token: 'keyword.$0' } ],
        [ /\bmedium\b/, { token: 'keyword.$0' } ],
        [ /\bhigh\b/, { token: 'keyword.$0' } ],
        [ /\bvery high\b/, { token: 'keyword.$0' } ],
        [ /\bdata translation implemented\b/, { token: 'keyword.$0' } ],
        [ /\bis fixed\b/, { token: 'keyword.$0' } ],
        [ /\binclude user fields\b/, { token: 'keyword.$0' } ],
        [ /\binitiating action\b/, { token: 'keyword.$0' } ],
        [ /\binvoking action\b/, { token: 'keyword.$0' } ],
        [ /\bbypass field rules\b/, { token: 'keyword.$0' } ],
        [ /\bis a DashBoardView\b/, { token: 'keyword.$0' } ],
        [ /\burl\b/, { token: 'keyword.$0' } ],
        [ /\bignore invalid addresses\b/, { token: 'keyword.$0' } ],
        [ /\bshow form only\b/, { token: 'keyword.$0' } ],
        [ /\bdialogue\b/, { token: 'keyword.$0' } ],
        [ /\buse array insert\b/, { token: 'keyword.$0' } ],
        [ /\btranslation\b/, { token: 'keyword.$0' } ],
        [ /\btranslations\b/, { token: 'keyword.$0' } ],
        [ /\bTranslation Field Rules\b/, { token: 'keyword.$0' } ],
        [ /\bsave on next\b/, { token: 'keyword.$0' } ],
        [ /\bimplements S3ClassicAttachments\b/, { token: 'keyword.$0' } ],
        [ /\bexact translation\b/, { token: 'keyword.$0' } ],
        [ /\bcontext of\b/, { token: 'keyword.$0' } ],
        [ /\bpage break\b/, { token: 'keyword.$0' } ],
        [ /\bindependent\b/, { token: 'keyword.$0' } ],
        [ /\btop align\b/, { token: 'keyword.$0' } ],
        [ /\bbottom align\b/, { token: 'keyword.$0' } ],
        [ /\bmiddle\b/, { token: 'keyword.$0' } ],
        [ /\bdisable AuditIndex\b/, { token: 'keyword.$0' } ],
        [ /\bimplements StaticJava\b/, { token: 'keyword.$0' } ],
        [ /\bis a DashBoardPanel\b/, { token: 'keyword.$0' } ],
        [ /\bis a Pane\b/, { token: 'keyword.$0' } ],
        [ /\blist\b/, { token: 'keyword.$0' } ],
        [ /\bshow preview of\b/, { token: 'keyword.$0' } ],
        [ /\buse for\b/, { token: 'keyword.$0' } ],
        [ /\bshow on\b/, { token: 'keyword.$0' } ],
        [ /\bis a StaticJavaWS\b/, { token: 'keyword.$0' } ],
        [ /\bis a StaticJavaPD\b/, { token: 'keyword.$0' } ],
        [ /\bsub form is\b/, { token: 'keyword.$0' } ],
        [ /\bdaily\b/, { token: 'keyword.$0' } ],
        [ /\bmonth name\b/, { token: 'keyword.$0' } ],
        [ /\bmonth short name\b/, { token: 'keyword.$0' } ],
        [ /\bday name\b/, { token: 'keyword.$0' } ],
        [ /\bday short name\b/, { token: 'keyword.$0' } ],
        [ /\bis a CalendarChart\b/, { token: 'keyword.$0' } ],
        [ /\bshow date of\b/, { token: 'keyword.$0' } ],
        [ /\bInitiateRequest Rules\b/, { token: 'keyword.$0' } ],
        [ /\bUpdateRequest Rules\b/, { token: 'keyword.$0' } ],
        [ /\bCancelRequest Rules\b/, { token: 'keyword.$0' } ],
        [ /\bpropagated from\b/, { token: 'keyword.$0' } ],
        [ /\bchart list\b/, { token: 'keyword.$0' } ],
        [ /\bchart elements\b/, { token: 'keyword.$0' } ],
        [ /\bchart periods\b/, { token: 'keyword.$0' } ],
        [ /\bmember of\b/, { token: 'keyword.$0' } ],
        [ /\bpeer group\b/, { token: 'keyword.$0' } ],
        [ /\bAppointments\b/, { token: 'keyword.$0' } ],
        [ /\bappointment\b/, { token: 'keyword.$0' } ],
        [ /\breminder\b/, { token: 'keyword.$0' } ],
        [ /\bbefore\b/, { token: 'keyword.$0' } ],
        [ /\blocation is\b/, { token: 'keyword.$0' } ],
        [ /\bsend as\b/, { token: 'keyword.$0' } ],
        [ /\bmeeting\b/, { token: 'keyword.$0' } ],
        [ /\bmessage\b/, { token: 'keyword.$0' } ],
        [ /\beffective stamp\b/, { token: 'keyword.$0' } ],
        [ /\bfvar\b/, { token: 'keyword.$0' } ],
        [ /\baction request id\b/, { token: 'keyword.$0' } ],
        [ /\bweeks\b/, { token: 'keyword.$0' } ],
        [ /\bApply Pending Effective Rules\b/, { token: 'keyword.$0' } ],
        [ /\bimplements InlineCreate\b/, { token: 'keyword.$0' } ],
        [ /\blimited list personalization\b/, { token: 'keyword.$0' } ],
        [ /\blimited form personalization\b/, { token: 'keyword.$0' } ],
        [ /\bfuture data indicator\b/, { token: 'keyword.$0' } ],
        [ /\bhidden entry\b/, { token: 'keyword.$0' } ],
        [ /\breason code not required\b/, { token: 'keyword.$0' } ],
        [ /\baction comment not required\b/, { token: 'keyword.$0' } ],
        [ /\beffective date not required\b/, { token: 'keyword.$0' } ],
        [ /\bsuppress current period\b/, { token: 'keyword.$0' } ],
        [ /\bhas future changes\b/, { token: 'keyword.$0' } ],
        [ /\bAction Exit Rules\b/, { token: 'keyword.$0' } ],
        [ /\bimplements PeriodFilter\b/, { token: 'keyword.$0' } ],
        [ /\bdate is\b/, { token: 'keyword.$0' } ],
        [ /\bdate range is\b/, { token: 'keyword.$0' } ],
        [ /\bperiod is\b/, { token: 'keyword.$0' } ],
        [ /\byear is\b/, { token: 'keyword.$0' } ],
        [ /\bcurrent date is\b/, { token: 'keyword.$0' } ],
        [ /\baudit transaction id\b/, { token: 'keyword.$0' } ],
        [ /\bForm Index\b/, { token: 'keyword.$0' } ],
        [ /\bActionForm Index\b/, { token: 'keyword.$0' } ],
        [ /\bList Index\b/, { token: 'keyword.$0' } ],
        [ /\bCardView Index\b/, { token: 'keyword.$0' } ],
        [ /\bimplicit\b/, { token: 'keyword.$0' } ],
        [ /\bconfigurable\b/, { token: 'keyword.$0' } ],
        [ /\bjar entry is\b/, { token: 'keyword.$0' } ],
        [ /\bline number is\b/, { token: 'keyword.$0' } ],
        [ /\bline offset is\b/, { token: 'keyword.$0' } ],
        [ /\bis a PageContextForm\b/, { token: 'keyword.$0' } ],
        [ /\bis a MenuForm\b/, { token: 'keyword.$0' } ],
        [ /\bauto refresh\b/, { token: 'keyword.$0' } ],
        [ /\bhas charts\b/, { token: 'keyword.$0' } ],
        [ /\bhas instance selection\b/, { token: 'keyword.$0' } ],
        [ /\baction form is\b/, { token: 'keyword.$0' } ],
        [ /\bprint form\b/, { token: 'keyword.$0' } ],
        [ /\bOntology Relations Info\b/, { token: 'keyword.$0' } ],
        [ /\bOntology Sets Info\b/, { token: 'keyword.$0' } ],
        [ /\bAutoSequenceRule Fields\b/, { token: 'keyword.$0' } ],
        [ /\bImplicit Relation Info\b/, { token: 'keyword.$0' } ],
        [ /\bimplements LightweightAuditing\b/, { token: 'keyword.$0' } ],
        [ /\bpre-calculate totals\b/, { token: 'keyword.$0' } ],
        [ /\bdynamically calculate totals\b/, { token: 'keyword.$0' } ],
        [ /\bnext record\b/, { token: 'keyword.$0' } ],
        [ /\bprevious record\b/, { token: 'keyword.$0' } ],
        [ /\bconfiguration:\"\b/, { token: 'keyword.$0' } ],
        [ /\bis last day in month\b/, { token: 'keyword.$0' } ],
        [ /\btranslation for\b/, { token: 'keyword.$0' } ],
        [ /\bfield\b/, { token: 'keyword.$0' } ],
        [ /\bform\b/, { token: 'keyword.$0' } ],
        [ /\bderive value from\b/, { token: 'keyword.$0' } ],
        [ /\ballow unlimited concurrency\b/, { token: 'keyword.$0' } ],
        [ /\bReports\b/, { token: 'keyword.$0' } ],
        [ /\bValid Business Classes\b/, { token: 'keyword.$0' } ],
        [ /\bdisable subset select on dependent relation\b/, { token: 'keyword.$0' } ],
        [ /\bimplements ActionResults\b/, { token: 'keyword.$0' } ],
        [ /\bdisable ActionResults\b/, { token: 'keyword.$0' } ],
        [ /\binclude persistent user fields\b/, { token: 'keyword.$0' } ],
        [ /\binclude auditable user fields\b/, { token: 'keyword.$0' } ],
        [ /\binclude derived user fields\b/, { token: 'keyword.$0' } ],
        [ /\bafter\b/, { token: 'keyword.$0' } ],
        [ /\ball dates\b/, { token: 'keyword.$0' } ],
        [ /\bkeypad is\b/, { token: 'keyword.$0' } ],
        [ /\bprompt for\b/, { token: 'keyword.$0' } ],
        [ /\bAttributes\b/, { token: 'keyword.$0' } ],
        [ /\ball crypto actions\b/, { token: 'keyword.$0' } ],
        [ /\bdisplay negative amounts using parens\b/, { token: 'keyword.$0' } ],
        [ /\bdisplay amounts in\b/, { token: 'keyword.$0' } ],
        [ /\bthousands\b/, { token: 'keyword.$0' } ],
        [ /\bmillions\b/, { token: 'keyword.$0' } ],
        [ /\bactor agent required\b/, { token: 'keyword.$0' } ],
        [ /\bURL Parameters\b/, { token: 'keyword.$0' } ],
        [ /\berror message key\b/, { token: 'keyword.$0' } ],
        [ /\bshow in title\b/, { token: 'keyword.$0' } ],
        [ /\bformat fields based on primary form\b/, { token: 'keyword.$0' } ],
        [ /\bsystem date\b/, { token: 'keyword.$0' } ],
        [ /\bchild is\b/, { token: 'keyword.$0' } ],
        [ /\baggregation percent is\b/, { token: 'keyword.$0' } ],
        [ /\bcompute\b/, { token: 'keyword.$0' } ],
        [ /\bcompute value\b/, { token: 'keyword.$0' } ],
        [ /\ball periods in\b/, { token: 'keyword.$0' } ],
        [ /\bAutoSequence Fields\b/, { token: 'keyword.$0' } ],
        [ /\bdimension name is\b/, { token: 'keyword.$0' } ],
        [ /\bas pdf\b/, { token: 'keyword.$0' } ],
        [ /\brow search field is\b/, { token: 'keyword.$0' } ],
        [ /\bin progress when\b/, { token: 'keyword.$0' } ],
        [ /\bcompleted when\b/, { token: 'keyword.$0' } ],
        [ /\beight column\b/, { token: 'keyword.$0' } ],
        [ /\bnine column\b/, { token: 'keyword.$0' } ],
        [ /\bten column\b/, { token: 'keyword.$0' } ],
        [ /\bcolumn8\b/, { token: 'keyword.$0' } ],
        [ /\bcolumn9\b/, { token: 'keyword.$0' } ],
        [ /\bcolumn10\b/, { token: 'keyword.$0' } ],
        [ /\bperiod label\b/, { token: 'keyword.$0' } ],
        [ /\bdistinct\b/, { token: 'keyword.$0' } ],
        [ /\braise\b/, { token: 'keyword.$0' } ],
        [ /\balert\b/, { token: 'keyword.$0' } ],
        [ /\bchild set is\b/, { token: 'keyword.$0' } ],
        [ /\bimplements InlineUserFields\b/, { token: 'keyword.$0' } ],
        [ /\bsize is\b/, { token: 'keyword.$0' } ],
        [ /\bvisited action is\b/, { token: 'keyword.$0' } ],
        [ /\bMapping Fields\b/, { token: 'keyword.$0' } ],
        [ /\bunique ID set\b/, { token: 'keyword.$0' } ],
        [ /\bimplements AdminCommand\b/, { token: 'keyword.$0' } ],
        [ /\bdisable filtering within db\b/, { token: 'keyword.$0' } ],
        [ /\bdynamic configuration\b/, { token: 'keyword.$0' } ],
        [ /\bbase url\b/, { token: 'keyword.$0' } ],
        [ /\bimplements ConfigurationMetadata\b/, { token: 'keyword.$0' } ],
        [ /\bchanged field names\b/, { token: 'keyword.$0' } ],
        [ /\brun after\b/, { token: 'keyword.$0' } ],
        [ /\bassign async action request id to\b/, { token: 'keyword.$0' } ],
        [ /\bis like\b/, { token: 'keyword.$0' } ],
        [ /\bcurrent async action request id\b/, { token: 'keyword.$0' } ],
        [ /\btype is like\b/, { token: 'keyword.$0' } ],
        [ /\bimplements BODId\b/, { token: 'keyword.$0' } ],
        [ /\bimplements ExportBOD\b/, { token: 'keyword.$0' } ],
        [ /\bExport Fields\b/, { token: 'keyword.$0' } ],
        [ /\bExclude Fields\b/, { token: 'keyword.$0' } ],
        [ /\bbod id\b/, { token: 'keyword.$0' } ],
        [ /\bbod key\b/, { token: 'keyword.$0' } ],
        [ /\bmeasure name is\b/, { token: 'keyword.$0' } ],
        [ /\btop node when\b/, { token: 'keyword.$0' } ],
        [ /\bdefault total node is\b/, { token: 'keyword.$0' } ],
        [ /\btrigger\b/, { token: 'keyword.$0' } ],
        [ /\bPA service\b/, { token: 'keyword.$0' } ],
        [ /\bbeginning balance is period zero\b/, { token: 'keyword.$0' } ],
        [ /\bsuppress when in dimension\b/, { token: 'keyword.$0' } ],
        [ /\binclude fields from\b/, { token: 'keyword.$0' } ],
        [ /\binclude persistent fields from\b/, { token: 'keyword.$0' } ],
        [ /\binclude auditable fields from\b/, { token: 'keyword.$0' } ],
        [ /\binclude user fields from\b/, { token: 'keyword.$0' } ],
        [ /\bQueue Mapping Fields\b/, { token: 'keyword.$0' } ],
        [ /\bdisplay as byte size\b/, { token: 'keyword.$0' } ],
        [ /\bdisplay using\b/, { token: 'keyword.$0' } ],
        [ /\bContext Message Invocations\b/, { token: 'keyword.$0' } ],
        [ /\bMessage\b/, { token: 'keyword.$0' } ],
        [ /\bProperty Mapping\b/, { token: 'keyword.$0' } ],
        [ /\bsend\b/, { token: 'keyword.$0' } ],
        [ /\ball lists\b/, { token: 'keyword.$0' } ],
        [ /\ball forms\b/, { token: 'keyword.$0' } ],
        [ /\bContextMessage Index\b/, { token: 'keyword.$0' } ],
        [ /\bis a ContextMessage\b/, { token: 'keyword.$0' } ],
        [ /\bis a ContextMessageInvocation\b/, { token: 'keyword.$0' } ],
        [ /\bhas references\b/, { token: 'keyword.$0' } ],
        [ /\bContextMessageInvocation Index\b/, { token: 'keyword.$0' } ],
        [ /\bshow total node\b/, { token: 'keyword.$0' } ],
        [ /\bupdate stamp key\b/, { token: 'keyword.$0' } ],
        [ /\bappointment message\b/, { token: 'keyword.$0' } ],
        [ /\bappointment attachment\b/, { token: 'keyword.$0' } ],
        [ /\bCube Relations\b/, { token: 'keyword.$0' } ],
        [ /\bcell relation to\b/, { token: 'keyword.$0' } ],
        [ /\bDimension Mapping\b/, { token: 'keyword.$0' } ],
        [ /\bPreload Measures\b/, { token: 'keyword.$0' } ],
        [ /\bcube dimension value\b/, { token: 'keyword.$0' } ],
        [ /\bimplements SecurityCache\b/, { token: 'keyword.$0' } ],
        [ /\binput value\b/, { token: 'keyword.$0' } ],
        [ /\bimplements IncrementalReplication\b/, { token: 'keyword.$0' } ],
        [ /\binvoke script\b/, { token: 'keyword.$0' } ],
        [ /\binvoke method\b/, { token: 'keyword.$0' } ],
        [ /\bin script\b/, { token: 'keyword.$0' } ],
        [ /\busing mime type\b/, { token: 'keyword.$0' } ],
        [ /\bhas role\b/, { token: 'keyword.$0' } ],
        [ /\bin group\b/, { token: 'keyword.$0' } ],
        [ /\bimplements RepresentativeImageView\b/, { token: 'keyword.$0' } ],
        [ /\bdisable multiple instance selection\b/, { token: 'keyword.$0' } ],
        [ /\bvalid for measure\b/, { token: 'keyword.$0' } ],
        [ /\bimplements IndependentFormPanels\b/, { token: 'keyword.$0' } ],
        [ /\bmatrix relation to\b/, { token: 'keyword.$0' } ],
        [ /\bperiod view is\b/, { token: 'keyword.$0' } ],
        [ /\bdynamic mapping is\b/, { token: 'keyword.$0' } ],
        [ /\buntranslatable\b/, { token: 'keyword.$0' } ],
        [ /\bdisable StaticTranslations\b/, { token: 'keyword.$0' } ],
        [ /\bdisable surrogates\b/, { token: 'keyword.$0' } ],
        [ /\bdescendants are\b/, { token: 'keyword.$0' } ],
        [ /\bshow context form on top\b/, { token: 'keyword.$0' } ],
        [ /\bMeasure Based Dimensions\b/, { token: 'keyword.$0' } ],
        [ /\bcaption is\b/, { token: 'keyword.$0' } ],
        [ /\bis aggregatable\b/, { token: 'keyword.$0' } ],
        [ /\buser configuration\b/, { token: 'keyword.$0' } ],
        [ /\bconfig stamp is\b/, { token: 'keyword.$0' } ],
        [ /\band gaps\b/, { token: 'keyword.$0' } ],
        [ /\bindependent form is\b/, { token: 'keyword.$0' } ],
        [ /\bdisplay as switch\b/, { token: 'keyword.$0' } ],
        [ /\bpanel header is\b/, { token: 'keyword.$0' } ],
        [ /\bdisplay as hijri date\b/, { token: 'keyword.$0' } ],
        [ /\bdisplay as gregorian date\b/, { token: 'keyword.$0' } ],
        [ /\brefresh using\b/, { token: 'keyword.$0' } ],
        [ /\baudit log\b/, { token: 'keyword.$0' } ],
        [ /\bmessage queue\b/, { token: 'keyword.$0' } ],
        [ /\blink\b/, { token: 'keyword.$0' } ],
        [ /\bto this agent\b/, { token: 'keyword.$0' } ],
        [ /\bsuppress manual transaction warning\b/, { token: 'keyword.$0' } ],
        [ /\bis an InstanceCountChart\b/, { token: 'keyword.$0' } ],
        [ /\bChart Fields\b/, { token: 'keyword.$0' } ],
        [ /\bembedded\b/, { token: 'keyword.$0' } ],
        [ /\b_reserved_1422\b/, { token: 'keyword.$0' } ],
        [ /\bchart is\b/, { token: 'keyword.$0' } ],
        [ /\bdynamic preload measures are\b/, { token: 'keyword.$0' } ],
        [ /\bvariable\b/, { token: 'keyword.$0' } ],
        [ /\bin background group\b/, { token: 'keyword.$0' } ],
        [ /\bbackground group\b/, { token: 'keyword.$0' } ],
        [ /\bis drill target\b/, { token: 'keyword.$0' } ],
        [ /\bwrite to base level only\b/, { token: 'keyword.$0' } ],
        [ /\ballow write to summary level\b/, { token: 'keyword.$0' } ],
        [ /\bSuppress Warnings\b/, { token: 'keyword.$0' } ],
        [ /\bset size on\b/, { token: 'keyword.$0' } ],
        [ /\ball set size warnings\b/, { token: 'keyword.$0' } ],
        [ /\bagent actor\b/, { token: 'keyword.$0' } ],
        [ /\benable explicit context override\b/, { token: 'keyword.$0' } ],
        [ /\brun outside of action background group\b/, { token: 'keyword.$0' } ],
        [ /\band attachable\b/, { token: 'keyword.$0' } ],
        [ /\band attachability\b/, { token: 'keyword.$0' } ],
        [ /\bis neither accessible nor attachable\b/, { token: 'keyword.$0' } ],
        [ /\bis text index\b/, { token: 'keyword.$0' } ],
        [ /\band blank dimension\b/, { token: 'keyword.$0' } ],
        [ /\bempty\b/, { token: 'keyword.$0' } ],
        [ /\brefresh and lock this instance\b/, { token: 'keyword.$0' } ],
        [ /\bcommit transaction\b/, { token: 'keyword.$0' } ],
        [ /\bmenu item name is\b/, { token: 'keyword.$0' } ],
        [ /\bfooter is\b/, { token: 'keyword.$0' } ],
        [ /\bassign async background group id to\b/, { token: 'keyword.$0' } ],
        [ /\bon error\b/, { token: 'keyword.$0' } ],
        [ /\bwith year of\b/, { token: 'keyword.$0' } ],
        [ /\byear when\b/, { token: 'keyword.$0' } ],
        [ /\bquarter when\b/, { token: 'keyword.$0' } ],
        [ /\bmonth when\b/, { token: 'keyword.$0' } ],
        [ /\bweek when\b/, { token: 'keyword.$0' } ],
        [ /\bday when\b/, { token: 'keyword.$0' } ],
        [ /\bcurrent date period is\b/, { token: 'keyword.$0' } ],
        [ /\bstart date is\b/, { token: 'keyword.$0' } ],
        [ /\bend date is\b/, { token: 'keyword.$0' } ],
        [ /\bparent period is\b/, { token: 'keyword.$0' } ],
        [ /\bnext period is\b/, { token: 'keyword.$0' } ],
        [ /\bprevious period is\b/, { token: 'keyword.$0' } ],
        [ /\bbeginning balance is\b/, { token: 'keyword.$0' } ],
        [ /\bending balance is\b/, { token: 'keyword.$0' } ],
        [ /\bbeginning ytd balance is\b/, { token: 'keyword.$0' } ],
        [ /\bending ytd balance is\b/, { token: 'keyword.$0' } ],
        [ /\bparameters\b/, { token: 'keyword.$0' } ],
        [ /\bmime type\b/, { token: 'keyword.$0' } ],
        [ /\bContext Field Values\b/, { token: 'keyword.$0' } ],
        [ /\bwhen value changed\b/, { token: 'keyword.$0' } ],
        [ /\bdisable DataTranslations\b/, { token: 'keyword.$0' } ],
        [ /\bprotected\b/, { token: 'keyword.$0' } ],
        [ /\bbypass relational integrity rules\b/, { token: 'keyword.$0' } ],
        [ /\benable ImmutableContextCache\b/, { token: 'keyword.$0' } ],
        [ /\bdisable ImmutableContextCache\b/, { token: 'keyword.$0' } ],
        [ /\bphrase\b/, { token: 'keyword.$0' } ],
        [ /\bshow fixed integer scale\b/, { token: 'keyword.$0' } ],
        [ /\bminimum value is\b/, { token: 'keyword.$0' } ],
        [ /\btop node is\b/, { token: 'keyword.$0' } ],
        [ /\bHierarchies\b/, { token: 'keyword.$0' } ],
        [ /\bbase level\b/, { token: 'keyword.$0' } ],
        [ /\bincluded\b/, { token: 'keyword.$0' } ],
        [ /\bbod reference\b/, { token: 'keyword.$0' } ],
        [ /\bnoun\b/, { token: 'keyword.$0' } ],
        [ /\bgeneral action\b/, { token: 'keyword.$0' } ],
        [ /\bSubtotal By\b/, { token: 'keyword.$0' } ],
        [ /\bbypass no duplicates validation\b/, { token: 'keyword.$0' } ],
        [ /\bdeprecated\b/, { token: 'keyword.$0' } ],
        [ /\ballow dimension reordering\b/, { token: 'keyword.$0' } ],
        [ /\binline create\b/, { token: 'keyword.$0' } ],
        [ /\blazily lock this instance\b/, { token: 'keyword.$0' } ],
        [ /\ballow form create\b/, { token: 'keyword.$0' } ],
        [ /\bpersistent user fields\b/, { token: 'keyword.$0' } ],
        [ /\bauditable user fields\b/, { token: 'keyword.$0' } ],
        [ /\bderived user fields\b/, { token: 'keyword.$0' } ],
        [ /\bthis field\b/, { token: 'keyword.$0' } ],
        [ /\bdependent panels\b/, { token: 'keyword.$0' } ],
        [ /\bproperty\b/, { token: 'keyword.$0' } ],
        [ /\bmultiple select\b/, { token: 'keyword.$0' } ],
        [ /\bsingle select\b/, { token: 'keyword.$0' } ],
        [ /\balways display search\b/, { token: 'keyword.$0' } ],
        [ /\binitially display search\b/, { token: 'keyword.$0' } ],
        [ /\bdisplay as star\b/, { token: 'keyword.$0' } ],
        [ /\bdisplay as box\b/, { token: 'keyword.$0' } ],
        [ /\bfilled star\b/, { token: 'keyword.$0' } ],
        [ /\boutlined star\b/, { token: 'keyword.$0' } ],
        [ /\bsynchronized\b/, { token: 'keyword.$0' } ],
        [ /\bshow as simple list\b/, { token: 'keyword.$0' } ],
        [ /\bkeyword search field is\b/, { token: 'keyword.$0' } ],
        [ /\binitially display\b/, { token: 'keyword.$0' } ],
        [ /\bassign async action background group id to\b/, { token: 'keyword.$0' } ],
        [ /\bcurrent action background group id\b/, { token: 'keyword.$0' } ],
        [ /\baction background group\b/, { token: 'keyword.$0' } ],
        [ /\bcurrent action background group\b/, { token: 'keyword.$0' } ],
        [ /\bdisable IncrementalReplication\b/, { token: 'keyword.$0' } ],
        [ /\bhttp method is\b/, { token: 'keyword.$0' } ],
        [ /\bplain text\b/, { token: 'keyword.$0' } ],
        [ /\bis condition for\b/, { token: 'keyword.$0' } ],
        [ /\bis related link for\b/, { token: 'keyword.$0' } ],
        [ /\bis related value for\b/, { token: 'keyword.$0' } ],
        [ /\bwhen blank\b/, { token: 'keyword.$0' } ],
        [ /\bshow using\b/, { token: 'keyword.$0' } ],
        [ /\bSoHo Xi\b/, { token: 'keyword.$0' } ],
        [ /\bSoHo 3.x\b/, { token: 'keyword.$0' } ],
        [ /\bfooter\b/, { token: 'keyword.$0' } ],
        [ /\blarge menu\b/, { token: 'keyword.$0' } ],
        [ /\bsmall menu\b/, { token: 'keyword.$0' } ],
        [ /\bin portrait\b/, { token: 'keyword.$0' } ],
        [ /\bin landscape\b/, { token: 'keyword.$0' } ],
        [ /\bfont offset is\b/, { token: 'keyword.$0' } ],
        [ /\bis transaction boundary\b/, { token: 'keyword.$0' } ],
        [ /\bunlimited\b/, { token: 'keyword.$0' } ],
        [ /\bdo not save in checkpoint\b/, { token: 'keyword.$0' } ],
        [ /\bsend ion bod\b/, { token: 'keyword.$0' } ],
        [ /\bbod is\b/, { token: 'keyword.$0' } ],
        [ /\bbod type is\b/, { token: 'keyword.$0' } ],
        [ /\bassign message id to\b/, { token: 'keyword.$0' } ],
        [ /\btemplate\b/, { token: 'keyword.$0' } ],
        [ /\bXMLDocument\b/, { token: 'keyword.$0' } ],
        [ /\bJSONObject\b/, { token: 'keyword.$0' } ],
        [ /\bselect\b/, { token: 'keyword.$0' } ],
        [ /\bis an Import Action\b/, { token: 'keyword.$0' } ],
        [ /\ball imports\b/, { token: 'keyword.$0' } ],
        [ /\bdocument for\b/, { token: 'keyword.$0' } ],
        [ /\bis a DrillList\b/, { token: 'keyword.$0' } ],
        [ /\bform mask is\b/, { token: 'keyword.$0' } ],
        [ /\blist mask is\b/, { token: 'keyword.$0' } ],
        [ /\bview navigation\b/, { token: 'keyword.$0' } ],
        [ /\bconfiguration\b/, { token: 'keyword.$0' } ],
        [ /\bis version relation\b/, { token: 'keyword.$0' } ],
        [ /\bKey Field Back Relations\b/, { token: 'keyword.$0' } ],
        [ /\bReplaced Relations\b/, { token: 'keyword.$0' } ],
        [ /\bReplaced Sets\b/, { token: 'keyword.$0' } ],
        [ /\bOntological OTM Relations\b/, { token: 'keyword.$0' } ],
        [ /\bAnalyticCube Info\b/, { token: 'keyword.$0' } ],
        [ /\bDimension Attribute\b/, { token: 'keyword.$0' } ],
        [ /\bMeasure Attribute\b/, { token: 'keyword.$0' } ],
        [ /\bDictionary Component Info\b/, { token: 'keyword.$0' } ],
        [ /\bAllow User Fields\b/, { token: 'keyword.$0' } ],
        [ /\bKey Field Reference Info\b/, { token: 'keyword.$0' } ],
        [ /\bAdd UsedByATransaction\b/, { token: 'keyword.$0' } ],
        [ /\bDelete Entrance Rules\b/, { token: 'keyword.$0' } ],
        [ /\bDelete Instance Rules\b/, { token: 'keyword.$0' } ],
        [ /\bByParentRelation\b/, { token: 'keyword.$0' } ],
        [ /\btransient or local dependent\b/, { token: 'keyword.$0' } ],
        [ /\buses primary set\b/, { token: 'keyword.$0' } ],
        [ /\bautomated context of\b/, { token: 'keyword.$0' } ],
        [ /\bOPF Snapshot Fields\b/, { token: 'keyword.$0' } ],
        [ /\bKey Field\b/, { token: 'keyword.$0' } ],
        [ /\btype\b/, { token: 'keyword.$0' } ],
        [ /\bCube Links\b/, { token: 'keyword.$0' } ],
        [ /\blink to\b/, { token: 'keyword.$0' } ],
        [ /\btop node\b/, { token: 'keyword.$0' } ],
        [ /\bInstanceCount\b/, { token: 'keyword.$0' } ],
        [ /\baggregation of\b/, { token: 'keyword.$0' } ],
        [ /\bcalculate dynamically\b/, { token: 'keyword.$0' } ],
        [ /\bimplements AuditCompare\b/, { token: 'keyword.$0' } ],
        [ /\bshow changed values on right\b/, { token: 'keyword.$0' } ],
        [ /\baudit compare\b/, { token: 'keyword.$0' } ],
        [ /\bcube rule is\b/, { token: 'keyword.$0' } ],
        [ /\bshow labels\b/, { token: 'keyword.$0' } ],
        [ /\bhide labels\b/, { token: 'keyword.$0' } ],
        [ /\bshort date\b/, { token: 'keyword.$0' } ],
        [ /\bmedium date\b/, { token: 'keyword.$0' } ],
        [ /\blong date\b/, { token: 'keyword.$0' } ],
        [ /\bshort anniversary\b/, { token: 'keyword.$0' } ],
        [ /\bmedium anniversary\b/, { token: 'keyword.$0' } ],
        [ /\blong anniversary\b/, { token: 'keyword.$0' } ],
        [ /\bshort period\b/, { token: 'keyword.$0' } ],
        [ /\bmedium period\b/, { token: 'keyword.$0' } ],
        [ /\blong period\b/, { token: 'keyword.$0' } ],
        [ /\bbypass integrity rules\b/, { token: 'keyword.$0' } ],
        [ /\bfilter list is\b/, { token: 'keyword.$0' } ],
        [ /\bfilter\b/, { token: 'keyword.$0' } ],
        [ /\bsort\b/, { token: 'keyword.$0' } ],
        [ /\bshow legend\b/, { token: 'keyword.$0' } ],
        [ /\bhide legend\b/, { token: 'keyword.$0' } ],
        [ /\bshow as donut\b/, { token: 'keyword.$0' } ],
        [ /\bshow bars\b/, { token: 'keyword.$0' } ],
        [ /\bvertically\b/, { token: 'keyword.$0' } ],
        [ /\bhorizontally\b/, { token: 'keyword.$0' } ],
        [ /\bshow elements\b/, { token: 'keyword.$0' } ],
        [ /\bside-by-side\b/, { token: 'keyword.$0' } ],
        [ /\bstacked\b/, { token: 'keyword.$0' } ],
        [ /\boverlapped\b/, { token: 'keyword.$0' } ],
        [ /\bshow area\b/, { token: 'keyword.$0' } ],
        [ /\bno records message is\b/, { token: 'keyword.$0' } ],
        [ /\bworkflow event title is\b/, { token: 'keyword.$0' } ],
        [ /\binsert history\b/, { token: 'keyword.$0' } ],
        [ /\bchange create date\b/, { token: 'keyword.$0' } ],
        [ /\bdelete history\b/, { token: 'keyword.$0' } ],
        [ /\bpurge audit log entries\b/, { token: 'keyword.$0' } ],
        [ /\bpurge date\b/, { token: 'keyword.$0' } ],
        [ /\btable\b/, { token: 'keyword.$0' } ],
        [ /\bcreate within\b/, { token: 'keyword.$0' } ],
        [ /\bcopy row\b/, { token: 'keyword.$0' } ],
        [ /\bcolumn headers\b/, { token: 'keyword.$0' } ],
        [ /\brow headers\b/, { token: 'keyword.$0' } ],
        [ /\blogical id\b/, { token: 'keyword.$0' } ],
        [ /\btenant id\b/, { token: 'keyword.$0' } ],
        [ /\binclude deleted records\b/, { token: 'keyword.$0' } ],
        [ /\binclude only deleted records\b/, { token: 'keyword.$0' } ],
        [ /\backnowledge action is\b/, { token: 'keyword.$0' } ],
        [ /\backnowledge completed when\b/, { token: 'keyword.$0' } ],
        [ /\bimplements InMemoryCache\b/, { token: 'keyword.$0' } ],
        [ /\bclear in-memory cache\b/, { token: 'keyword.$0' } ],
        [ /\blast panel navigation is\b/, { token: 'keyword.$0' } ],
        [ /\bnext button title is\b/, { token: 'keyword.$0' } ],
        [ /\bmingle url is\b/, { token: 'keyword.$0' } ],
        [ /\bperiod dimension is\b/, { token: 'keyword.$0' } ],
        [ /\bis a RuleBlocks\b/, { token: 'keyword.$0' } ],
        [ /\bRuleBlock Index\b/, { token: 'keyword.$0' } ],
        [ /\breplace\b/, { token: 'keyword.$0' } ],
        [ /\bno acknowledgement\b/, { token: 'keyword.$0' } ],
        [ /\bproduct line\b/, { token: 'keyword.$0' } ],
        [ /\bbase environment\b/, { token: 'keyword.$0' } ],
        [ /\bCSVText\b/, { token: 'keyword.$0' } ],
        [ /\bdbimport\b/, { token: 'keyword.$0' } ],
        [ /\binto\b/, { token: 'keyword.$0' } ],
        [ /\bimplements OnceUsedNeverDelete\b/, { token: 'keyword.$0' } ],
        [ /\bdrill around\b/, { token: 'keyword.$0' } ],
        [ /\bmanual range is\b/, { token: 'keyword.$0' } ],
        [ /\bdisplay when\b/, { token: 'keyword.$0' } ],
        [ /\bimplements DynamicChildVisibility\b/, { token: 'keyword.$0' } ],
        [ /\bchild designator field is\b/, { token: 'keyword.$0' } ],
        [ /\bparent match field is\b/, { token: 'keyword.$0' } ],
        [ /\btarget business class is\b/, { token: 'keyword.$0' } ],
        [ /\bsuppress header\b/, { token: 'keyword.$0' } ],
        [ /\bsuppress footer\b/, { token: 'keyword.$0' } ],
        [ /\bTranslation Rules\b/, { token: 'keyword.$0' } ],
        [ /\bimplements ColumnarReplicate\b/, { token: 'keyword.$0' } ],
        [ /\bimplements ColumnarView\b/, { token: 'keyword.$0' } ],
        [ /\bresponsive card view is\b/, { token: 'keyword.$0' } ],
        [ /\bimplements LongRunningList\b/, { token: 'keyword.$0' } ],
        [ /\bRequired Search Parameters\b/, { token: 'keyword.$0' } ],
        [ /\bcurrent corporate time\b/, { token: 'keyword.$0' } ],
        [ /\bcurrent corporate date\b/, { token: 'keyword.$0' } ],
        [ /\bcurrent corporate year\b/, { token: 'keyword.$0' } ],
        [ /\bcurrent corporate period\b/, { token: 'keyword.$0' } ],
        [ /\bcurrent corporate aniversary\b/, { token: 'keyword.$0' } ],
        [ /\bsystem current corporate date\b/, { token: 'keyword.$0' } ],
        [ /\bsystem current corporate year\b/, { token: 'keyword.$0' } ],
        [ /\bsystem current corporate period\b/, { token: 'keyword.$0' } ],
        [ /\bsystem current corporate aniversary\b/, { token: 'keyword.$0' } ],
        [ /\bimplements VirtualBackRelSets\b/, { token: 'keyword.$0' } ],
        [ /\bindicator field is\b/, { token: 'keyword.$0' } ],
        [ /\breplicate when\b/, { token: 'keyword.$0' } ],
        [ /\bthen set to\b/, { token: 'keyword.$0' } ],
        [ /\bwhen in background\b/, { token: 'keyword.$0' } ],
        [ /\bselect line that\b/, { token: 'keyword.$0' } ],
        [ /\bstarts with\b/, { token: 'keyword.$0' } ],
        [ /\bends with\b/, { token: 'keyword.$0' } ],
        [ /\bline in\b/, { token: 'keyword.$0' } ],
        [ /\bsplit\b/, { token: 'keyword.$0' } ],
        [ /\bsplit on\b/, { token: 'keyword.$0' } ],
        [ /\bin base64\b/, { token: 'keyword.$0' } ],
        [ /\berror message field name\b/, { token: 'keyword.$0' } ],
        [ /\bprivileged\b/, { token: 'keyword.$0' } ],
        [ /\binitial\b/, { token: 'keyword.$0' } ],
        [ /\bschedule concurrency is\b/, { token: 'keyword.$0' } ],
        [ /\bFullConcurrency\b/, { token: 'keyword.$0' } ],
        [ /\bNoConcurrency\b/, { token: 'keyword.$0' } ],
        [ /\bNoActionGroupConcurrency\b/, { token: 'keyword.$0' } ],
        [ /\bmisfire strategy is\b/, { token: 'keyword.$0' } ],
        [ /\bDoNothing\b/, { token: 'keyword.$0' } ],
        [ /\bRunOnce\b/, { token: 'keyword.$0' } ],
        [ /\bRunAllProcessesScheduled\b/, { token: 'keyword.$0' } ],
        [ /\bshow context form on left\b/, { token: 'keyword.$0' } ],
        [ /\bfull name\b/, { token: 'keyword.$0' } ],
        [ /\bbackground is\b/, { token: 'keyword.$0' } ],
        [ /\btheme color\b/, { token: 'keyword.$0' } ],
        [ /\bdouble width\b/, { token: 'keyword.$0' } ],
        [ /\btriple width\b/, { token: 'keyword.$0' } ],
        [ /\bquad width\b/, { token: 'keyword.$0' } ],
        [ /\bdouble height\b/, { token: 'keyword.$0' } ],
        [ /\bManualRepresentation\b/, { token: 'keyword.$0' } ],
        [ /\bManual Representation\b/, { token: 'keyword.$0' } ],
        [ /\brepresentation name is\b/, { token: 'keyword.$0' } ],
        [ /\bmanual representation\b/, { token: 'keyword.$0' } ],
        [ /\bis a ManualRepresentation\b/, { token: 'keyword.$0' } ],
        [ /\brandom background\b/, { token: 'keyword.$0' } ],
        [ /\bforeground text is\b/, { token: 'keyword.$0' } ],
        [ /\bis default sort order\b/, { token: 'keyword.$0' } ],
        [ /\bdisplay as select\b/, { token: 'keyword.$0' } ],
        [ /\berror message stack trace\b/, { token: 'keyword.$0' } ],
        [ /\btemplate name\b/, { token: 'keyword.$0' } ],
        [ /\binvoke modify update stamp\b/, { token: 'keyword.$0' } ],
        [ /\bexpect acknowledgement\b/, { token: 'keyword.$0' } ],
        [ /\baccounting entity is\b/, { token: 'keyword.$0' } ],
        [ /\bdocument id is\b/, { token: 'keyword.$0' } ],
        [ /\bvariation id is\b/, { token: 'keyword.$0' } ],
        [ /\brevision id is\b/, { token: 'keyword.$0' } ],
        [ /\bsource is\b/, { token: 'keyword.$0' } ],
        [ /\binstance count is\b/, { token: 'keyword.$0' } ],
        [ /\bcustom property is\b/, { token: 'keyword.$0' } ],
        [ /\bround to precision\b/, { token: 'keyword.$0' } ],
        [ /\bdisplay as tag\b/, { token: 'keyword.$0' } ],
        [ /\bcenter field is\b/, { token: 'keyword.$0' } ],
        [ /\blinear when\b/, { token: 'keyword.$0' } ],
        [ /\bpurple\b/, { token: 'keyword.$0' } ],
        [ /\bturquoise\b/, { token: 'keyword.$0' } ],
        [ /\battach\b/, { token: 'keyword.$0' } ],
        [ /\bis a MultiListView\b/, { token: 'keyword.$0' } ],
        [ /\bfirst page header is\b/, { token: 'keyword.$0' } ],
        [ /\bpage header is\b/, { token: 'keyword.$0' } ],
        [ /\bpage footer is\b/, { token: 'keyword.$0' } ],
        [ /\benable subset select on dependent relation\b/, { token: 'keyword.$0' } ],
        [ /\blog\b/, { token: 'keyword.$0' } ],
        [ /\bpage mask is\b/, { token: 'keyword.$0' } ],
        [ /\btemplate mask is\b/, { token: 'keyword.$0' } ],
        [ /\bview mask is\b/, { token: 'keyword.$0' } ],

        // identifiers and keywords
        [ /[a-zA-Z_$][\w$]*/, {
          cases: {
            '@keywords': { token: 'keyword.$0' },
            '@default': 'identifier'
          }
        } ],

        // whitespace
        { include: '@whitespace' },

        // delimiters and operators
        [ /[{}()\[\]]/, '@brackets' ],
        [ /[<>](?!@symbols)/, '@brackets' ],
        [ /@symbols/, {
          cases: {
            '@operators': 'delimiter',
            '@default': ''
          }
        } ],

        // @ annotations.
        [ /@\s*[a-zA-Z_\$][\w\$]*/, 'annotation' ],

        // numbers
        [ /(@digits)[eE]([\-+]?(@digits))?[fFdD]?/, 'number.float' ],
        [ /(@digits)\.(@digits)([eE][\-+]?(@digits))?[fFdD]?/, 'number.float' ],
        [ /0[xX](@hexdigits)[Ll]?/, 'number.hex' ],
        [ /0(@octaldigits)[Ll]?/, 'number.octal' ],
        [ /0[bB](@binarydigits)[Ll]?/, 'number.binary' ],
        [ /(@digits)[fFdD]/, 'number.float' ],
        [ /(@digits)[lL]?/, 'number' ],

        // delimiter: after number because of .\d floats
        [ /[;,.]/, 'delimiter' ],

        // strings
        [ /"([^"\\]|\\.)*$/, 'string.invalid' ],  // non-terminated string
        [ /"/, 'string', '@string' ],

        // characters
        [ /'[^\\']'/, 'string' ],
        [ /(')(@escapes)(')/, [ 'string', 'string.escape', 'string' ] ],
        [ /'/, 'string.invalid' ]
      ],

      whitespace: [
        [ /[ \t\r\n]+/, '' ],
        [ /\/\*\*(?!\/)/, 'comment.doc', '@javadoc' ],
        [ /\/\*/, 'comment', '@comment' ],
        [ /\/\/.*$/, 'comment' ],
      ],

      comment: [
        [ /[^\/*]+/, 'comment' ],
        // [/\/\*/, 'comment', '@push' ],    // nested comment not allowed :-(
        // [/\/\*/,    'comment.invalid' ],    // this breaks block comments in the shape of /* //*/
        [ /\*\//, 'comment', '@pop' ],
        [ /[\/*]/, 'comment' ]
      ],
      // Identical copy of comment above, except for the addition of .doc
      javadoc: [
        [ /[^\/*]+/, 'comment.doc' ],
        // [/\/\*/, 'comment.doc', '@push' ],    // nested comment not allowed :-(
        [ /\/\*/, 'comment.doc.invalid' ],
        [ /\*\//, 'comment.doc', '@pop' ],
        [ /[\/*]/, 'comment.doc' ]
      ],

      string: [
        [ /[^\\"]+/, 'string' ],
        [ /@escapes/, 'string.escape' ],
        [ /\\./, 'string.escape.invalid' ],
        [ /"/, 'string', '@pop' ]
      ],
    },
  };

  SECCLASS_LANGUAGE = <monaco.languages.IMonarchLanguage>{
    defaultToken: '',
    tokenPostfix: '.secclass',

    keywords: [
      'abstract', 'continue', 'for', 'new', 'switch', 'assert', 'default',
      'goto', 'package', 'synchronized', 'boolean', 'do', 'if', 'private',
      'this', 'break', 'double', 'implements', 'protected', 'throw', 'byte',
      'else', 'import', 'public', 'throws', 'case', 'enum', 'instanceof', 'return',
      'transient', 'catch', 'extends', 'int', 'short', 'try', 'char', 'final',
      'interface', 'static', 'void', 'class', 'finally', 'long', 'strictfp',
      'volatile', 'const', 'float', 'native', 'super', 'while', 'true', 'false',
    ],

    operators: [
      '=', '>', '<', '!', '~', '?', ':',
      '==', '<=', '>=', '!=', '&&', '||', '++', '--',
      '+', '-', '*', '/', '&', '|', '^', '%', '<<',
      '>>', '>>>', '+=', '-=', '*=', '/=', '&=', '|=',
      '^=', '%=', '<<=', '>>=', '>>>='
    ],

    // we include these common regular expressions
    symbols: /[=><!~?:&|+\-*\/\^%]+/,
    escapes: /\\(?:[abfnrtv\\"']|x[0-9A-Fa-f]{1,4}|u[0-9A-Fa-f]{4}|U[0-9A-Fa-f]{8})/,
    digits: /\d+(_+\d+)*/,
    octaldigits: /[0-7]+(_+[0-7]+)*/,
    binarydigits: /[0-1]+(_+[0-1]+)*/,
    hexdigits: /[[0-9a-fA-F]+(_+[0-9a-fA-F]+)*/,

    // The main tokenizer for our languages
    tokenizer: {
      root: [
        // landmark keywords
        [ /\bis a\b/, { token: 'keyword.$0' } ],
        [ /\bSecurityClass\b/, { token: 'keyword.$0' } ],
        [ /\bdescription is\b/, { token: 'keyword.$0' } ],
        [ /\bAccess Rights\b/, { token: 'keyword.$0' } ],
        [ /\bis accessible\b/, { token: 'keyword.$0' } ],
        [ /\bis not accessible\b/, { token: 'keyword.$0' } ],
        [ /\bin\b/, { token: 'keyword.$0' } ],
        [ /\bpast\b/, { token: 'keyword.$0' } ],
        [ /\bcurrent\b/, { token: 'keyword.$0' } ],
        [ /\bfuture\b/, { token: 'keyword.$0' } ],
        [ /\bwhen\b/, { token: 'keyword.$0' } ],
        [ /\bunconditionally\b/, { token: 'keyword.$0' } ],
        [ /\bexcluding\b/, { token: 'keyword.$0' } ],
        // actions
        [ /\ball actions\b/, { token: 'keyword.$0' } ],
        [ /\ball auditviews\b/, { token: 'keyword.$0' } ],
        [ /\ball creates\b/, { token: 'keyword.$0' } ],
        [ /\ball crypto actions\b/, { token: 'keyword.$0' } ],
        [ /\ball deletes\b/, { token: 'keyword.$0' } ],
        [ /\ball functions\b/, { token: 'keyword.$0' } ],
        [ /\ball imports\b/, { token: 'keyword.$0' } ],
        [ /\ball inquiries\b/, { token: 'keyword.$0' } ],
        [ /\ball updates\b/, { token: 'keyword.$0' } ],
        [ /\baudit create\b/, { token: 'keyword.$0' } ],
        [ /\baudit delete\b/, { token: 'keyword.$0' } ],
        [ /\baudit update\b/, { token: 'keyword.$0' } ],
        [ /\bchange create date\b/, { token: 'keyword.$0' } ],
        [ /\bchange effective date\b/, { token: 'keyword.$0' } ],
        [ /\bdata export\b/, { token: 'keyword.$0' } ],
        [ /\bdelete history\b/, { token: 'keyword.$0' } ],
        [ /\binquire\b/, { token: 'keyword.$0' } ],
        [ /\binsert history\b/, { token: 'keyword.$0' } ],
        [ /\binvalidate\b/, { token: 'keyword.$0' } ],
        [ /\bpurge audit log entries\b/, { token: 'keyword.$0' } ],
        [ /\bViewAuditLog\b/, { token: 'keyword.$0' } ],
        [ /\bViewFullAuditLog\b/, { token: 'keyword.$0' } ],
        [ /\ball navigations\b/, { token: 'keyword.$0' } ],
        [ /\ball global UI configuration\b/, { token: 'keyword.$0' } ],
        [ /\ball security configuration\b/, { token: 'keyword.$0' } ],
        [ /\ball personalization\b/, { token: 'keyword.$0' } ],
        [ /\blimited list personalization\b/, { token: 'keyword.$0' } ],
        [ /\blimited form personalization\b/, { token: 'keyword.$0' } ],
        [ /\bdata menu\b/, { token: 'keyword.$0' } ],
        [ /\ball scheduled actions\b/, { token: 'keyword.$0' } ],
        [ /\bfuture data indicator\b/, { token: 'keyword.$0' } ],
        // types
        [ /\bType\b/, { token: 'keyword.$0' } ],
        [ /\bBusinessClass\b/, { token: 'keyword.$0' } ],
        [ /\bBusinessTask\b/, { token: 'keyword.$0' } ],
        [ /\bDataArea\b/, { token: 'keyword.$0' } ],
        [ /\bExecutable\b/, { token: 'keyword.$0' } ],
        [ /\bField\b/, { token: 'keyword.$0' } ],
        [ /\bFieldGroups\b/, { token: 'keyword.$0' } ],
        [ /\bKeyFIeld\b/, { token: 'keyword.$0' } ],
        [ /\bMenu\b/, { token: 'keyword.$0' } ],
        [ /\bMenuItem\b/, { token: 'keyword.$0' } ],
        [ /\bModule\b/, { token: 'keyword.$0' } ],
        [ /\bWebApp\b/, { token: 'keyword.$0' } ],
        [ /\bgrants access\b/, { token: 'keyword.$0' } ],
        [ /\bto all ontology\b/, { token: 'keyword.$0' } ],
        [ /\band attachable\b/, { token: 'keyword.$0' } ],


        // identifiers and keywords
        [ /[a-zA-Z_$][\w$]*/, {
          cases: {
            '@keywords': { token: 'keyword.$0' },
            '@default': 'identifier'
          }
        } ],

        // whitespace
        { include: '@whitespace' },

        // delimiters and operators
        [ /[{}()\[\]]/, '@brackets' ],
        [ /[<>](?!@symbols)/, '@brackets' ],
        [ /@symbols/, {
          cases: {
            '@operators': 'delimiter',
            '@default': ''
          }
        } ],

        // @ annotations.
        [ /@\s*[a-zA-Z_\$][\w\$]*/, 'annotation' ],

        // numbers
        [ /(@digits)[eE]([\-+]?(@digits))?[fFdD]?/, 'number.float' ],
        [ /(@digits)\.(@digits)([eE][\-+]?(@digits))?[fFdD]?/, 'number.float' ],
        [ /0[xX](@hexdigits)[Ll]?/, 'number.hex' ],
        [ /0(@octaldigits)[Ll]?/, 'number.octal' ],
        [ /0[bB](@binarydigits)[Ll]?/, 'number.binary' ],
        [ /(@digits)[fFdD]/, 'number.float' ],
        [ /(@digits)[lL]?/, 'number' ],

        // delimiter: after number because of .\d floats
        [ /[;,.]/, 'delimiter' ],

        // strings
        [ /"([^"\\]|\\.)*$/, 'string.invalid' ],  // non-terminated string
        [ /"/, 'string', '@string' ],

        // characters
        [ /'[^\\']'/, 'string' ],
        [ /(')(@escapes)(')/, [ 'string', 'string.escape', 'string' ] ],
        [ /'/, 'string.invalid' ]
      ],

      whitespace: [
        [ /[ \t\r\n]+/, '' ],
        [ /\/\*\*(?!\/)/, 'comment.doc', '@javadoc' ],
        [ /\/\*/, 'comment', '@comment' ],
        [ /\/\/.*$/, 'comment' ],
      ],

      comment: [
        [ /[^\/*]+/, 'comment' ],
        // [/\/\*/, 'comment', '@push' ],    // nested comment not allowed :-(
        // [/\/\*/,    'comment.invalid' ],    // this breaks block comments in the shape of /* //*/
        [ /\*\//, 'comment', '@pop' ],
        [ /[\/*]/, 'comment' ]
      ],
      // Identical copy of comment above, except for the addition of .doc
      javadoc: [
        [ /[^\/*]+/, 'comment.doc' ],
        // [/\/\*/, 'comment.doc', '@push' ],    // nested comment not allowed :-(
        [ /\/\*/, 'comment.doc.invalid' ],
        [ /\*\//, 'comment.doc', '@pop' ],
        [ /[\/*]/, 'comment.doc' ]
      ],

      string: [
        [ /[^\\"]+/, 'string' ],
        [ /@escapes/, 'string.escape' ],
        [ /\\./, 'string.escape.invalid' ],
        [ /"/, 'string', '@pop' ]
      ],
    },
  };

  secclassAccessibilityTenses: Array<string> = [ 'past', 'current', 'future' ];
  secclassActions: Array<string> = [ 'all actions', 'all functions', 'all creates', 'all imports', 'all scheduled actions', 'all security configuration', 'all audit views',
    'all global UI configuration', 'all updates', 'all personalization', 'all inquiries', 'all deletes', 'all audit views', 'future data indicator', 'data export',
    'insert history', 'ViewAuditLog', 'audit update', 'data menu', 'change create date', 'purge audit log entries', 'Inquire', 'audit delete', 'all crypto actions',
    'ViewFullAuditLog', 'change effective date', 'invalidate', 'limited list personalization', 'audit create', 'limited form personalization', 'delete history' ];

  public getSecuritySnippet(category: SecClassRuleType, snippet: SecClassType | SecClassObject): string {
    if (category === 'object') {
      return this.getSecurableObjectSnippet(snippet as SecClassObject, false);
    }

    if (category === 'type') {
      return this.getSecurableTypeSnippet(snippet as SecClassType, false);
    }
  }

  private async getSecClassCompletionList(model: ITextModel, position: Position, context: CompletionContext, token: CancellationToken) {
    const suggestions = [];
    await this._buildSecurityCompletionList('Employee', suggestions, model, position);
    // ok to not type the promise return, monaco also takes a promise here!
    return <ProviderResult<CompletionList>>{ suggestions };
  }

  private async _buildSecurityCompletionList(businessClass: string, suggestions: Array<string>, model, position){
    const rule = this._parseSecuritySection(model, position);
    const isActionFor = model.getLineContent(position.lineNumber).trim().startsWith('for ');
    const isExcludingAction = position.lineNumber > 1 ? model.getLineContent(position.lineNumber - 1).trim().startsWith('excluding') : false;

    if (rule?.type === 'object' && rule?.ruleTypeName === 'BusinessClass' &&  (isActionFor || isExcludingAction)) {
      const actionList = await this.getSecurableActionsList(rule.objectName, rule.ruleTypeName).toPromise();

      actionList.forEach(action => {
        suggestions.push(this.buildCompletedItem(action));
      });
    }

    this.buildStaticSecuritySuggestions(model, position, suggestions);
  }

  private buildStaticSecuritySuggestions(model: ITextModel, position: Position, suggestions: Array<string>) {
    const underAccessRights = this.indentedUnderAccessRights(model, position);
    const firstTextColumn = model.getLineFirstNonWhitespaceColumn(position.lineNumber);
    const lastTextColumn = model.getLineLastNonWhitespaceColumn(position.lineNumber);
    const line = model.getLineContent(position.lineNumber).trim();

    if (position.column === 5 && !underAccessRights) {
      suggestions.push(this.buildCompletedItem('description is'));
      suggestions.push(this.buildCompletedItem('Access Rights'));
    }

    if (position.column === 13 && underAccessRights) {
      suggestions.push(this.buildCompletedItem('is accessible'));
      suggestions.push(this.buildCompletedItem('is not accessible'));
    }

    if (firstTextColumn === 13 && underAccessRights) {
      if (line.endsWith('is')) {
        suggestions.push(this.buildCompletedItem('not'));
      }
      if (line.endsWith('is') || line.endsWith('not')) {
        suggestions.push(this.buildCompletedItem('accessible'));
      }
      if (line.endsWith('accessible')) {
        suggestions.push(this.buildCompletedItem('in'));
      }
      if (line.endsWith('in') || line.endsWith(',')) {
        this.secclassAccessibilityTenses.forEach(tense => {
          if (line.indexOf(tense) === -1) {
            suggestions.push(this.buildCompletedItem(tense));
          }
        });
      }
    }

    if (position.column === 17 && underAccessRights) {
      suggestions.push(this.buildCompletedItem('for'));
      suggestions.push(this.buildCompletedItem('when'));
      suggestions.push(this.buildCompletedItem('unconditionally'));
    }

    if (position.column === 9 && underAccessRights &&  firstTextColumn === 0 &&  lastTextColumn === 0) {
      suggestions.push(this.buildSnippet(Soho.Locale.translate('LPL_EDITOR_MODULE_SNIPPET'), this.getSecurableObjectSnippet('Module')));
      suggestions.push(this.buildSnippet(Soho.Locale.translate('LPL_EDITOR_DATAAREA_SNIPPET'), this.getSecurableObjectSnippet('DataArea')));
      suggestions.push(this.buildSnippet(Soho.Locale.translate('LPL_EDITOR_WEBAPP_SNIPPET'), this.getSecurableObjectSnippet('WebApp')));
      suggestions.push(this.buildSnippet(Soho.Locale.translate('LPL_EDITOR_MENU_SNIPPET'), this.getSecurableObjectSnippet('Menu')));
      suggestions.push(this.buildSnippet(Soho.Locale.translate('LPL_EDITOR_MENUITEM_SNIPPET'), this.getSecurableObjectSnippet('MenuItem')));
      suggestions.push(this.buildSnippet(Soho.Locale.translate('LPL_EDITOR_KEYFIELD_SNIPPET'), this.getSecurableObjectSnippet('KeyField')));
      suggestions.push(this.buildSnippet(Soho.Locale.translate('LPL_EDITOR_BUSINESSCLASS_SNIPPET'), this.getSecurableObjectSnippet('BusinessClass')));
      suggestions.push(this.buildSnippet(Soho.Locale.translate('LPL_EDITOR_FIELD_SNIPPET'), this.getSecurableObjectSnippet('Field')));
      suggestions.push(this.buildSnippet(Soho.Locale.translate('LPL_EDITOR_ALLFIELDS_SNIPPET'), this.getSecurableObjectSnippet('AllFields')));
      suggestions.push(this.buildSnippet(Soho.Locale.translate('LPL_EDITOR_BUSINESSTASK_SNIPPET'), this.getSecurableObjectSnippet('BusinessTask')));
      suggestions.push(this.buildSnippet(Soho.Locale.translate('LPL_EDITOR_EXECUTABLE_SNIPPET'), this.getSecurableObjectSnippet('Executable')));
    }
  }

  private buildCompletedItem(item: string): any {
    return         {
      label: item,
      kind: monaco.languages.CompletionItemKind.Text,
      insertText: item,
      insertTextRules: monaco.languages.CompletionItemInsertTextRule,
    };
  }

  private buildSnippet(label: string, snippet: string): any {
    return       {
      label,
      kind: monaco.languages.CompletionItemKind.Snippet,
      insertText: snippet,
      insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
      documentation: label
    };
  }

  private indentedUnderAccessRights(model: ITextModel, position: Position): boolean {
    for (let i = position.lineNumber; i > 0; i--) {
      const lineContent = model.getLineContent(i);
      if (lineContent?.trim().startsWith('Access Rights')) {
        return true;
      }
    }

    return false;
  }

  private getSecurableObjectSnippet(securableObject: SecClassObject, forSnippet = true): string {

    if ('MenuItem' === securableObject) {
      if (forSnippet) {
        return [
          '<' + securableObject + '> ' + securableObject + ' for <Menu> Menu',
          '\tis accessible',
          '\t\tfor all actions',
          '\t\t\texcluding',
          '\t\t\t\t// list excluded actions here!',
          '\t\tunconditionally',
          '\t\t//when (write your condition here)'
        ].join('\n');
      } else {
        return [
          '<' + securableObject + '> ' + securableObject + ' for <Menu> Menu',
          '\t\t\tis accessible',
          '\t\t\t\tfor all actions',
          '\t\t\t\t\texcluding',
          '\t\t\t\t\t\t// list excluded actions here!',
          '\t\t\t\tunconditionally',
          '\t\t\t\t//when (write your condition here)'
        ].join('\n');
      }
    }

    if ('AllFields' === securableObject) {
      if (forSnippet) {
        return [
          'All Fields for <BusinessClass> BusinessClass',
          '\texcluding',
          '\t\t//list excluded fields here',
          '\tis accessible',
          '\t\tfor all actions',
          '\t\t\texcluding',
          '\t\t\t\t// list excluded actions here!',
          '\t\tunconditionally',
          '\t\t//when (write your condition here)'
        ].join('\n');
      } else {
        return [
          'All Fields for <BusinessClass> BusinessClass',
          '\t\t\texcluding',
          '\t\t\t\t//list excluded fields here',
          '\t\t\tis accessible',
          '\t\t\t\tfor all actions',
          '\t\t\t\t\texcluding',
          '\t\t\t\t\t\t// list excluded actions here!',
          '\t\t\t\tunconditionally',
          '\t\t\t\t//when (write your condition here)'
        ].join('\n');
      }
    }

    if ('KeyField' === securableObject) {
      if (forSnippet) {
        return [
          '<KeyField> KeyField',
          '\tgrants access in past,current,future',
          '\t\tto all ontology// list of business classes or keyFields',
          '\t\tfor all actions',
          '\t\t\texcluding',
          '\t\t\t\t// list excluded actions here!'
        ].join('\n');
      } else {
        return [
          '<KeyField> KeyField',
          '\t\t\tgrants access in past,current,future',
          '\t\t\t\tto all ontology// list of business classes or keyFields',
          '\t\t\t\tfor all actions',
          '\t\t\t\t\texcluding',
          '\t\t\t\t\t\t// list excluded actions here!'
        ].join('\n');
      }
    }
    if ('DataArea' === securableObject) {
      if (forSnippet) {
        return [
          '@this ' + securableObject,
          '\tis accessible',
          '\t\tfor all actions',
          '\t\t\texcluding',
          '\t\t\t\t// list excluded actions here!',
          '\t\tunconditionally',
          '\t\t//when (write your condition here)'
        ].join('\n');
      } else {
        return [
          '@this ' + securableObject,
          '\t\t\tis accessible',
          '\t\t\t\tfor all actions',
          '\t\t\t\t\texcluding',
          '\t\t\t\t\t\t// list excluded actions here!',
          '\t\t\t\tunconditionally',
          '\t\t\t\t//when (write your condition here)'
        ].join('\n');
      }
    }

    if (forSnippet) {
      return [
        '<' + securableObject + '> ' + securableObject,
        '\tis accessible',
        '\t\tfor all actions',
        '\t\t\texcluding',
        '\t\t\t\t// list excluded actions here!',
        '\t\tunconditionally',
        '\t\t//when (write your condition here)'
      ].join('\n');
    } else {
      return [
        '<' + securableObject + '> ' + securableObject,
        '\t\t\tis accessible',
        '\t\t\t\tfor all actions',
        '\t\t\t\t\texcluding',
        '\t\t\t\t\t\t// list excluded actions here!',
        '\t\t\t\tunconditionally',
        '\t\t\t\t//when (write your condition here)'
      ].join('\n');
    }
  }

  private getSecurableTypeSnippet(securableType: SecClassType, forSnippet = true): string {

    if (forSnippet) {
      return [
        securableType + ' Type',
        '\tis accessible',
        '\t\tfor all actions',
        '\t\t\texcluding',
        '\t\t\t\t// list excluded actions here!',
        '\t\tunconditionally',
        '\t\t//when (write your condition here)'
      ].join('\n');
    } else {
      return [
        securableType + ' Type',
        '\t\t\tis accessible',
        '\t\t\t\tfor all actions',
        '\t\t\t\t\texcluding',
        '\t\t\t\t\t\t// list excluded actions here!',
        '\t\t\t\tunconditionally',
        '\t\t\t\t//when (write your condition here)'
      ].join('\n');
    }
  }

  public parseSecuritySection(model: IEditorModel, position: Position): SecClassRule {
    const textModel: ITextModel = model as ITextModel;
    return this._parseSecuritySection(textModel, position);
  }

  private _parseSecuritySection(model: ITextModel, position: Position): SecClassRule {
    if (this.indentedUnderAccessRights(model, position)) {
      for (let i = position.lineNumber; i > 0; i--) {
        const lpl = model.getLineContent(i);
        const tokens = this.tokenizeString(lpl);
        const rule = this._parseSecurityThing(tokens);

        if (rule) {return rule;}
      }
    }

    return undefined;
  }

  private _parseSecurityThing(tokens: Array<string>): SecClassRule {
    if (tokens.length > 1) {
      const s1 = tokens[0].trim();
      const s2 = tokens[1].trim();

      if ('Type' === s2 && this.isSecurableTypeKeyword(s1)) {
        return  { type: 'type', ruleTypeName: s1 as SecClassType };
      }

      if (this.isSecurableObjectKeyword(s2)) {
        return  { type: 'object', ruleTypeName: s2 as SecClassObject, objectName: s1 };
      } else if (s1 === 'All' && s2 === 'Fields') {
        const busClass = tokens.length > 3 ? tokens[3] : '';
        return  { type: 'object', ruleTypeName: 'AllFields', objectName: busClass };
      }
    }
    return undefined;
  }

  private isSecurableObjectKeyword(str): boolean {
    return LMLplEditorService.SecClassObjectKeyWords.includes(str);
  }

  private isSecurableTypeKeyword(str): boolean {
    return LMLplEditorService.SecClassTypeKeyWords.includes(str);
  }

  private tokenizeString(s: string): Array<string> {
    return s.split(' ').filter(value => value.trim().length > 0);
  }

  public buildBaseConfigurationUrl(): string {
    const url =  this.lmUrlUtilService.getBaseUrl() + '/config/All';
    return this.lmUrlUtilService.updateContextSessionKeyParams(url);
  }

  public getSecurableActionsList(secObjectName: string, secObjectType: string): Observable<Array<string>> {
    return new Observable<Array<string>>(observer => {
      const actions = new Array<string>();
      const url = this.buildBaseConfigurationUrl();
      this.lmFormRequestService.getSecurableActionsList(url, secObjectName, secObjectType)
        .subscribe((result: any) => {
          if (Array.isArray(result)) {
            result.forEach((a) => actions.push(a.name));
          }
          observer.next(actions);
          observer.complete();
        },
        (error: any) => {
          observer.next(actions);
          observer.complete();
        });
    });
  }

  public getSecurableObjectsList(secObjectType: string, containment: string): Observable<Array<string>> {
    return new Observable<Array<string>>(observer => {
      const objects = new Array<string>();
      const url = this.buildBaseConfigurationUrl();
      this.lmFormRequestService.getSecurableObjectsList(url, secObjectType, containment)
        .subscribe((result: any) => {
          if (Array.isArray(result)) {
            result.forEach((a) => objects.push(a.name));
          }
          observer.next(objects);
          observer.complete();
        },
        (error: any) => {
          observer.next(objects);
          observer.complete();
        });
    });
  }

  public openRepositoryHelperDialog(options: LMRepositoryHelperOptions): Observable<LMRepositoryHelperResponse> {
    return new Observable<LMRepositoryHelperResponse>(observer => {
      let dlgComponent: LMRepositoryHelperComponent;
      // eslint-disable-next-line prefer-const
      let dialog: LMModalDialogWrapperRef<LMRepositoryHelperComponent>;
      const formName = 'lm-repository-helper';
      const buttons = [];
      buttons.push(
        {
          id: this.lmUniqueIdService.uniqueId([ formName, LMDialogButtonKey.CLOSE ]),
          text: Soho.Locale.translate(LMDialogButtonKey.CLOSE),
          click: () => {
            dialog.close();
          },
        }
      );
      buttons.push(
        {
          id: this.lmUniqueIdService.uniqueId([ formName, '', LMDialogButtonKey.OK ]),
          text: Soho.Locale.translate('Insert'),
          click: () => {
            dlgComponent.validateLpl().subscribe(valid => {
              if (valid) {
                dialog.close(<LMRepositoryHelperResponse> {
                  lpl:  dlgComponent.buildLpl(),
                });
              }
            });

          },
          isDefault: true,
        }
      );

      let mode;
      if (options.request === 'snippet') {
        mode = Soho.Locale.translate('LPL_EDITOR_SNIPPETS');
      } else if (options.request === 'objects') {
        mode = Soho.Locale.translate('LPL_EDITOR_OBJECT_LOOKUP');
      } else if (options.request === 'actions') {
        mode = Soho.Locale.translate('LPL_EDITOR_ACTIONS');
      } else if (options.request === 'fields') {
        mode = Soho.Locale.translate('LPL_EDITOR_FIELDS');
      } else if (options.request === 'ontology') {
        mode = Soho.Locale.translate('LPL_EDITOR_Ontology');
      } else if (options.request === 'conditionBuilder') {
        mode = Soho.Locale.translate('LPL_EDITOR_CONDITION_BUILDER');
      }
      const attributes: SohoAttributesArray = [ { name:  'data-automation-id',  value: 'lm-repository-helper-modal' } ];
      const dialogOptions: SohoModalOptions = { fullsize: 'responsive', attributes };
      const title = mode ? this.lmFormatUtilService.formatMessage(Soho.Locale.translate('LPL_EDITOR_LPL_HELPER_DIALOG_TITLE'), mode) : Soho.Locale.translate('LPL_EDITOR_LPL', mode) ;

      dialog = this.lmModalWrapperService.modal(LMRepositoryHelperComponent, dialogOptions)
        .title(title)
        .buttons(buttons)
        .isAlert(false)
        .open()
        .apply((dialogComponent: LMRepositoryHelperComponent) => {
          dlgComponent = dialogComponent;
          dialogComponent.options = options;
        })
        .afterClose((result) => {
          observer.next(result);
          observer.complete();
        });
    });
  }
}
